import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  Building2,
  CalendarClock,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Eye,
  FileText,
  LayoutDashboard,
  MapPin,
  Plus,
  Search,
  Send,
  Settings,
  Store,
  Trash2,
  User,
  Users,
  X,
} from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/tables/data-table';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { apiRequest, getQueryFn } from '@/lib/queryClient';
import { Stepper, type Step } from '@/components/ui/stepper';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type AuditType = 'full' | 'posting_only' | 'removal_only';
type AuditQuestionType = 'yes_no' | 'text' | 'photo' | 'number';
type AuditQuestionScope = 'per_store' | 'per_route' | 'per_audit';

interface AuditCandidateCycle {
  lockId: number;
  cycleId: number;
  cycleName: string;
  retailerId: number;
  retailerName: string;
  startDate: string | null;
  endDate: string | null;
  cycleStatus: string | null;
  lockedAt: string | null;
  lockVersion: number;
  retailerLogo: string | null;
  storeCount: number;
  operatorCount: number;
  campaignCount: number;
  expectedLineCount: number;
  expectedQuantity: number;
}

interface AuditStore {
  id: number;
  name: string;
  address: string | null;
  region: string | null;
  campaignCount: number;
  expectedLineCount: number;
  expectedQuantity: number;
  originalOperators: Array<{ id: number; name: string }>;
}

interface Auditor {
  id: number;
  name: string;
  email: string;
  role: string;
  roles: string[];
  region: string | null;
}

interface AuditCampaign {
  id: number;
  name: string;
  auditType: AuditType;
  auditStartDate: string | null;
  status: string;
  retailerName: string;
  cycleName: string;
  startDate: string | null;
  endDate: string | null;
  storeCount: number;
  auditorCount: number;
  findingCount: number;
  complianceScore: number | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditCampaignStore {
  id: number;
  storeId: number;
  storeName: string;
  storeAddress: string | null;
  storeRegion: string | null;
  auditorId: number;
  auditorName: string;
  auditorEmail: string | null;
  expectedCampaignCount: number;
  expectedLineCount: number;
  expectedQuantity: number;
  originalOperators: Array<{ id: number; name: string | null }>;
  resultStatus: 'not_started' | 'in_progress' | 'submitted' | 'reviewed';
  findingCount: number;
  complianceScore: number | null;
  submittedAt: string | null;
  campaignResults: AuditStoreCampaignResult[];
  auditResponse?: any;
}

interface AuditStoreCampaignResult {
  key: string;
  storeId?: number;
  campaignId: number | null;
  campaignName: string;
  taskName: string | null;
  auditType: 'Posting' | 'Removal';
  formatId: number | null;
  formatName: string;
  expectedQuantity: number;
  approvedQuantity: number;
  submittedQuantity: number | null;
  condition: string | null;
  comments: string;
  evidenceAssetIds: string[];
  submissionStatus: 'not_submitted' | 'submitted' | 'approved' | 'rejected';
  postingEventId: number | null;
  imageUrl: string | null;
  imageUrls: string[];
  imageCount: number;
  reviewedAt: string | null;
  submittedAt: string | null;
  submittedNotes: string | null;
  reviewDecision: 'APPROVE' | 'REJECT' | null;
  reviewedByName: string | null;
  reviewAdminNotes: string | null;
  reviewFeedbackText: string | null;
  reviewRejectionNotes: string | null;
}

interface AuditCampaignDetail {
  audit: AuditCampaign & {
    retailerId: number;
    cycleId: number;
    lockedCycleId: number;
    createdByName: string | null;
  };
  stores: AuditCampaignStore[];
  template: AuditTemplate | null;
  surveyResponses: unknown[];
}

interface AuditTemplateQuestion {
  id: string;
  label: string;
  type: AuditQuestionType;
  scope: AuditQuestionScope;
  required: boolean;
}

interface AuditTemplateSection {
  id: string;
  title: string;
  phase?: 'briefing' | 'closure';
  questions: AuditTemplateQuestion[];
}

interface AuditTemplate {
  id: number;
  name: string;
  auditType: AuditType;
  openingText: string;
  closingText: string;
  sections: AuditTemplateSection[];
  isDefault: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AuditTemplateDraft {
  id?: number;
  name: string;
  auditType: AuditType;
  openingText: string;
  closingText: string;
  sections: AuditTemplateSection[];
  isDefault?: boolean;
}

interface AdminAuditsOverview {
  summary: {
    plannedAudits: number;
    activeAudits: number;
    completedAudits: number;
    openFindings: number;
    criticalFindings: number;
    averageComplianceScore: number | null;
    auditReadyCycles: number;
  };
  candidateCycles: AuditCandidateCycle[];
  auditCampaigns: AuditCampaign[];
  stores?: any[];
  campaignLines?: any[];
  findings: unknown[];
}

interface CycleStoresResponse {
  stores: AuditStore[];
}

interface AuditorsResponse {
  auditors: Auditor[];
}

interface AuditTemplatesResponse {
  templates: AuditTemplate[];
}

interface AuditDispatch {
  id: number;
  auditorId: number;
  auditorName: string;
  auditorEmail: string;
  status: 'draft' | 'sent' | 'in_progress' | 'submitted' | 'recalled';
  includeBriefing: boolean;
  openingAcknowledgedAt: string | null;
  sentAt: string | null;
  recalledAt: string | null;
  resentAt: string | null;
  updatedAt: string | null;
  storeCount: number;
  submittedStoreCount: number;
}

interface AuditDetailResponse {
  audit: AuditCampaignDetail['audit'];
  stores: AuditCampaignStore[];
  template: AuditTemplate | null;
  surveyResponses: unknown[];
  dispatches?: AuditDispatch[];
}

const AUDIT_TYPE_LABELS: Record<AuditType, string> = {
  full: 'Full Audit (Posting and Removal)',
  posting_only: 'Posting Only',
  removal_only: 'Removal Only',
};

const QUESTION_TYPE_LABELS: Record<AuditQuestionType, string> = {
  yes_no: 'Yes / No',
  text: 'Text',
  photo: 'Photo',
  number: 'Number',
};

const QUESTION_SCOPE_LABELS: Record<AuditQuestionScope, string> = {
  per_store: 'Per store',
  per_route: 'Per auditor route',
  per_audit: 'Per audit campaign',
};

const QUESTION_TYPES: AuditQuestionType[] = [
  'yes_no',
  'text',
  'photo',
  'number',
];

const QUESTION_SCOPES: AuditQuestionScope[] = [
  'per_store',
  'per_route',
  'per_audit',
];

function makeDraftId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function getTodayInputDate() {
  return new Date().toISOString().slice(0, 10);
}

function createEmptyTemplateDraft(): AuditTemplateDraft {
  return {
    name: 'New Audit Template',
    auditType: 'full',
    openingText: '',
    closingText: '',
    sections: [
      {
        id: makeDraftId('section'),
        title: 'New section',
        questions: [
          {
            id: makeDraftId('question'),
            label: 'New question',
            type: 'yes_no',
            scope: 'per_store',
            required: true,
          },
        ],
      },
    ],
  };
}

function cloneTemplate(template: AuditTemplate): AuditTemplateDraft {
  return {
    id: template.id,
    name: template.name,
    auditType: template.auditType,
    openingText: template.openingText ?? '',
    closingText: template.closingText ?? '',
    isDefault: template.isDefault,
    sections: template.sections.map((section) => ({
      ...section,
      questions: section.questions.map((question) => ({
        ...question,
        scope: question.scope ?? 'per_store',
      })),
    })),
  };
}

function countTemplateQuestions(template: Pick<AuditTemplate, 'sections'> | AuditTemplateDraft) {
  return template.sections.reduce((count, section) => count + section.questions.length, 0);
}

function getFakeExpectedItems(auditType: AuditType) {
  const postingItems = [
    { kind: 'Posting', title: 'Summer Value Poster', detail: 'Window poster A1 · Qty 2' },
    { kind: 'Posting', title: 'Fresh Offers Shelf Talker', detail: 'Aisle end display · Qty 6' },
  ];
  const removalItems = [
    { kind: 'Removal', title: 'Spring Promo Poster', detail: 'Window poster A1 · Qty 1' },
    { kind: 'Removal', title: 'Clearance Wobbler Set', detail: 'Checkout bay · Qty 4' },
  ];

  if (auditType === 'posting_only') return postingItems;
  if (auditType === 'removal_only') return removalItems;
  return [...postingItems, ...removalItems];
}

function getFakeCampaigns(auditType: AuditType) {
  const campaign1 = {
    id: 'camp-1',
    name: 'Summer Value Campaign',
    items: [
      { id: 'f-1', kind: 'Posting', title: 'Summer Value Poster', detail: 'Window poster A1', expectedQty: 2 },
      { id: 'f-2', kind: 'Removal', title: 'Spring Promo Poster', detail: 'Window poster A1', expectedQty: 1 },
    ],
    bgGradient: 'from-amber-400 to-orange-500',
    tag: 'SUMMER VALUE',
  };
  const campaign2 = {
    id: 'camp-2',
    name: 'Fresh Offers & Clearance',
    items: [
      { id: 'f-3', kind: 'Posting', title: 'Fresh Offers Shelf Talker', detail: 'Aisle end display', expectedQty: 6 },
      { id: 'f-4', kind: 'Removal', title: 'Clearance Wobbler Set', detail: 'Checkout bay', expectedQty: 4 },
    ],
    bgGradient: 'from-emerald-400 to-teal-600',
    tag: 'FRESH & CLEAR',
  };

  if (auditType === 'posting_only') {
    return [
      { ...campaign1, items: campaign1.items.filter(i => i.kind === 'Posting') },
      { ...campaign2, items: campaign2.items.filter(i => i.kind === 'Posting') },
    ].filter(c => c.items.length > 0);
  }
  if (auditType === 'removal_only') {
    return [
      { ...campaign1, items: campaign1.items.filter(i => i.kind === 'Removal') },
      { ...campaign2, items: campaign2.items.filter(i => i.kind === 'Removal') },
    ].filter(c => c.items.length > 0);
  }
  return [campaign1, campaign2];
}

const fakeStores = [
  { id: 'blackrock', name: 'SV Blackrock', address: 'Frascati Road, Blackrock, Co. Dublin', status: 'staged' },
  { id: 'dundrum', name: 'Tesco Extra Dundrum', address: 'Dundrum Town Centre, Dublin 16', status: 'staged' }
];

function AuditTemplateMobilePreview({ template }: { template: AuditTemplateDraft | null }) {
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [briefingAcknowledged, setBriefingAcknowledged] = useState(false);
  const [showBriefingScreen, setShowBriefingScreen] = useState(false);
  const [selectedMobileCampaignId, setSelectedMobileCampaignId] = useState<string | null>(null);
  const [showSignOffModal, setShowSignOffModal] = useState(false);
  const [signOffExpanded, setSignOffExpanded] = useState(false);

  // Store-specific states so they don't leak between stores in the simulator
  const [completedStores, setCompletedStores] = useState<Record<string, boolean>>({});
  const [checklistAnswers, setChecklistAnswers] = useState<Record<string, boolean>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [conditions, setConditions] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<Record<string, boolean>>({});
  const [closingRemarks, setClosingRemarks] = useState<Record<string, string>>({});
  const [closureAnswers, setClosureAnswers] = useState<Record<string, any>>({});
  const [completedCampaigns, setCompletedCampaigns] = useState<Record<string, boolean>>({});
  
  // Signature specific simulator state
  const [signoffMethod, setSignoffMethod] = useState<Record<string, 'signature' | 'typed_name'>>({});
  const [managerName, setManagerName] = useState<Record<string, string>>({});
  const [signatureData, setSignatureData] = useState<Record<string, boolean>>({});

  const expectedCampaigns = useMemo(() => {
    return template ? getFakeCampaigns(template.auditType) : [];
  }, [template?.auditType]);

  // Reset state when template changes
  useEffect(() => {
    setBriefingAcknowledged(false);
    setShowBriefingScreen(false);
    setSelectedMobileCampaignId(null);
    setSelectedStoreId(null);
    setShowSignOffModal(false);
    setSignOffExpanded(false);
    setCompletedStores({});
    setChecklistAnswers({});
    setQuantities({});
    setConditions({});
    setPhotos({});
    setClosingRemarks({});
    setClosureAnswers({});
    setCompletedCampaigns({});
    setSignoffMethod({});
    setManagerName({});
    setSignatureData({});
  }, [template]);

  const briefingSections = template?.sections.filter((s) => s.phase === 'briefing' || s.id === 'briefing') ?? [];

  const closingSections = template?.sections.filter(
    (s) => s.id !== 'briefing' && s.phase !== 'briefing' && s.id !== 'core-rules'
  ) ?? [];

  const coreRules = template?.sections.find((s) => s.id === 'core-rules');
  const requirePhotosOnMismatch = coreRules?.questions.find((q) => q.id === 'require-discrepancy-photos')?.required !== false;
  const enableConditionLogging = coreRules?.questions.find((q) => q.id === 'enable-condition-logging')?.required !== false;

  const handleIncrement = (id: string, max: number) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.min((prev[id] ?? max) + 1, max + 5),
    }));
  };

  const handleDecrement = (id: string) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max((prev[id] ?? 1) - 1, 0),
    }));
  };

  // Determine active view screen based on current state parameters
  let activeScreen: 'briefing' | 'stores_list' | 'store_details' | 'campaign_details' | 'sign_off' = 'stores_list';
  if (!briefingAcknowledged || showBriefingScreen) {
    activeScreen = 'briefing';
  } else if (selectedStoreId === null) {
    activeScreen = 'stores_list';
  } else if (selectedMobileCampaignId !== null) {
    activeScreen = 'campaign_details';
  } else if (showSignOffModal) {
    activeScreen = 'sign_off';
  } else {
    activeScreen = 'store_details';
  }

  const selectedStore = fakeStores.find(s => s.id === selectedStoreId);
  const storeCampaignsCompleted = selectedStoreId ? expectedCampaigns.filter(c => completedCampaigns[`${selectedStoreId}-${c.id}`]).length : 0;
  const isStoreReadyForSignature = selectedStoreId && storeCampaignsCompleted === expectedCampaigns.length;
  const isStoreAlreadySigned = selectedStoreId && completedStores[selectedStoreId] === true;

  const getCampaignProgress = (campaign: ReturnType<typeof getFakeCampaigns>[0]) => {
    let verifiedCount = 0;
    campaign.items.forEach(item => {
      const itemKey = `${selectedStoreId}-${campaign.id}-${item.id}`;
      const expectedQty = item.expectedQty;
      const verifiedQty = quantities[itemKey] !== undefined ? quantities[itemKey] : expectedQty;
      const hasPhoto = photos[itemKey] === true;
      const isDiscrepancy = verifiedQty !== expectedQty;

      const photoOk = !isDiscrepancy || !requirePhotosOnMismatch || hasPhoto;
      if (photoOk) {
        verifiedCount++;
      }
    });
    return { verifiedCount, totalCount: campaign.items.length };
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex w-full items-center justify-between px-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Interactive Mobile Preview
        </p>
        {template && (
          <Badge variant="secondary">{AUDIT_TYPE_LABELS[template.auditType]}</Badge>
        )}
      </div>

      <div className="w-[300px] shrink-0 rounded-[36px] bg-gray-900 p-2.5 shadow-2xl">
        <div className="overflow-hidden rounded-[28px] bg-black" style={{ height: 620 }}>
          {/* Status Bar */}
          <div className="flex items-center justify-between bg-gray-900 px-5 py-1.5 text-[10px] text-white">
            <span className="font-semibold">9:41</span>
            <span className="opacity-60">{'\u25CF\u25CF\u25CF'} WiFi {'\u25A0'}</span>
          </div>

          <div className="flex flex-col bg-gray-50" style={{ height: 'calc(100% - 28px)' }}>
            {/* Header */}
            <div className="flex shrink-0 items-center gap-2 px-3 py-2" style={{ backgroundColor: '#0F3A3F' }}>
              {/* Back Button rules based on current active screen */}
              {((activeScreen === 'briefing' && briefingAcknowledged) ||
                activeScreen === 'store_details' ||
                activeScreen === 'campaign_details' ||
                activeScreen === 'sign_off') && (
                <button
                  type="button"
                  onClick={() => {
                    if (activeScreen === 'campaign_details') {
                      setSelectedMobileCampaignId(null);
                    } else if (activeScreen === 'sign_off') {
                      setShowSignOffModal(false);
                    } else if (activeScreen === 'briefing') {
                      setShowBriefingScreen(false);
                    } else if (activeScreen === 'store_details') {
                      setSelectedStoreId(null);
                    }
                  }}
                  className="text-white hover:opacity-85 transition-opacity shrink-0 mr-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              <span className="flex-1 truncate text-xs font-semibold text-white">
                {activeScreen === 'briefing'
                  ? 'Operator Briefing'
                  : activeScreen === 'campaign_details'
                    ? expectedCampaigns.find(c => c.id === selectedMobileCampaignId)?.name 
                    : activeScreen === 'sign_off'
                      ? 'Manager Sign-Off'
                      : activeScreen === 'store_details'
                        ? selectedStore?.name 
                        : 'My Assigned Stores'}
              </span>
              <span className="shrink-0 rounded bg-white/20 px-1.5 py-0.5 text-[9px] font-medium text-white">
                {selectedStoreId ? 'Active' : 'Route'}
              </span>
            </div>

            {!template ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
                <FileText className="h-8 w-8 text-gray-300" />
                <p className="text-xs text-gray-400">Select a template to preview.</p>
              </div>
            ) : activeScreen === 'briefing' ? (
              /* STAGE 1: ROUTE-LEVEL OPERATOR BRIEFING SCREEN (GATED ON COLD START) */
              <div className="min-h-0 flex-1 flex flex-col justify-between p-3 space-y-3 overflow-y-auto">
                <div className="space-y-3">
                  {/* Retailer Info Bar */}
                  <div className="flex justify-between gap-3 px-3 py-2 bg-white border rounded-xl shadow-sm text-left">
                    <div className="flex-1">
                      <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">Retailer</p>
                      <p className="text-[10px] font-bold text-gray-900 truncate">SuperValu (Demo)</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">Version</p>
                      <p className="text-[10px] font-bold text-gray-900">Jun 4, 2026, 3:17 PM</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Please review briefing
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-950 font-medium">
                      This cycle has briefing content for operators. Read through the sections before starting your route.
                    </p>
                  </div>

                  <div className="rounded-xl border bg-white p-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Auditor Briefing Instructions</p>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-700 font-medium font-sans whitespace-pre-wrap">
                      {template.openingText || 'Review the cycle expectation brief. Please ensure you satisfy all site briefing safety rules before commencing your counts.'}
                    </p>
                  </div>

                  {briefingSections.map((section) => (
                    <div key={section.id} className="rounded-xl border bg-white p-3 shadow-sm space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{section.title}</p>
                      <div className="space-y-1.5 text-[11px] text-gray-700">
                        {section.questions.map((q) => (
                          <label key={q.id} className="flex items-start gap-2 cursor-pointer py-1">
                            <Checkbox
                              className="mt-0.5"
                              checked={!!checklistAnswers[q.id]}
                              onCheckedChange={(val) =>
                                setChecklistAnswers((prev) => ({ ...prev, [q.id]: val === true }))
                              }
                            />
                            <span className="leading-tight">{q.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  type="button" 
                  onClick={() => {
                    setBriefingAcknowledged(true);
                    setShowBriefingScreen(false);
                  }}
                  className="w-full rounded-xl py-2.5 text-xs font-semibold text-white shadow-sm flex items-center justify-center gap-1.5 transition-transform active:scale-[0.98]" 
                  style={{ backgroundColor: '#0F3A3F' }}
                >
                  <span>{briefingAcknowledged ? 'Continue to Route' : 'Acknowledge and Continue'}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : activeScreen === 'stores_list' ? (
              /* ROUTE OVERVIEW / ASSIGNED STORES LIST */
              <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Assigned Stores List
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowBriefingScreen(true)}
                    className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full font-bold bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 transition-colors"
                  >
                    <FileText className="h-3 w-3" />
                    <span>View Briefing</span>
                  </button>
                </div>
                <div className="space-y-2">
                  {fakeStores.map((store) => {
                    const isSigned = completedStores[store.id] === true;

                    return (
                      <button
                        key={store.id}
                        type="button"
                        onClick={() => {
                          setSelectedStoreId(store.id);
                        }}
                        className="w-full rounded-xl border bg-white p-3 shadow-sm hover:border-primary/50 text-left transition-all flex items-center justify-between gap-2 hover:scale-[1.01] active:scale-[0.99]"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-gray-900 truncate">
                            {store.name}
                          </p>
                          <p className="text-[9px] text-gray-500 mt-0.5 truncate">
                            {store.address}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className={cn(
                            "text-[8px] px-1.5 py-0.5 rounded-full border font-bold capitalize",
                            isSigned 
                              ? "bg-green-50 text-green-700 border-green-200" 
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          )}>
                            {isSigned ? 'Signed Off' : store.status}
                          </span>
                          <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : activeScreen === 'campaign_details' ? (
              /* STAGE 2: TASK/CAMPAIGN DETAILS VERIFICATION WINDOW */
              <div className="min-h-0 flex-1 flex flex-col justify-between overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 space-y-3">
                  {(() => {
                    const camp = expectedCampaigns.find(c => c.id === selectedMobileCampaignId);
                    if (!camp) return null;

                    const campProgress = getCampaignProgress(camp);
                    const isCampProgressDone = campProgress.verifiedCount === campProgress.totalCount;

                    return (
                      <div className="space-y-3 animate-in fade-in">
                        <div className={cn(
                          "w-full h-24 rounded-xl bg-gradient-to-br flex flex-col justify-end p-3 relative overflow-hidden shadow-inner",
                          camp.bgGradient
                        )}>
                          <div className="absolute top-3 right-3 rounded bg-black/40 px-1.5 py-0.5 text-[8px] font-semibold text-white uppercase tracking-wider">
                            Live Campaign Mockup
                          </div>
                          <p className="text-white text-xs font-black tracking-wider leading-tight uppercase drop-shadow">
                            {camp.tag}
                          </p>
                          <p className="text-white/90 text-[9px] font-medium mt-0.5">
                            {camp.name}
                          </p>
                        </div>

                        <div className="space-y-3">
                          {camp.items.map((item) => {
                            const itemKey = `${selectedStoreId}-${camp.id}-${item.id}`;
                            const expectedQty = item.expectedQty;
                            const verifiedQty = quantities[itemKey] !== undefined ? quantities[itemKey] : expectedQty;
                            const isDiscrepancy = verifiedQty !== expectedQty;
                            const condition = conditions[itemKey] ?? 'good';
                            const hasPhoto = photos[itemKey] === true;

                            return (
                              <div key={itemKey} className="rounded-xl border bg-white p-3 shadow-sm space-y-2 text-left">
                                <div className="flex items-start justify-between gap-2 border-b pb-2">
                                  <div>
                                    <Badge className={cn("text-[8px] py-0 px-1 font-semibold capitalize", item.kind === 'Posting' ? 'bg-emerald-50 text-emerald-800 border border-emerald-100 hover:bg-emerald-50' : 'bg-amber-50 text-amber-800 border border-amber-100 hover:bg-amber-50')} variant="outline">
                                      {item.kind}
                                    </Badge>
                                    <p className="mt-1 text-xs font-semibold text-gray-900">{item.title}</p>
                                    <p className="text-[9px] text-gray-500">{item.detail}</p>
                                  </div>
                                  <span className="text-[10px] font-medium text-gray-400 bg-gray-50 border px-1.5 py-0.5 rounded shrink-0">
                                    Exp: {expectedQty}
                                  </span>
                                </div>

                                <div className="grid grid-cols-[1fr_auto] items-center gap-2 pt-1 text-[11px]">
                                  <span className="text-gray-600 font-medium">Verify Qty Present:</span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="h-6 w-6 rounded-full border bg-slate-50 flex items-center justify-center font-bold hover:bg-slate-100 active:scale-95"
                                      onClick={() => handleDecrement(itemKey)}
                                    >
                                      -
                                    </button>
                                    <span className={cn("w-4 text-center font-semibold text-xs", isDiscrepancy && "text-amber-600 font-bold")}>
                                      {verifiedQty}
                                    </span>
                                    <button
                                      type="button"
                                      className="h-6 w-6 rounded-full border bg-slate-50 flex items-center justify-center font-bold hover:bg-slate-100 active:scale-95"
                                      onClick={() => handleIncrement(itemKey, expectedQty)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                {enableConditionLogging && (
                                  <div className="grid grid-cols-[1fr_auto] items-center gap-2 text-[11px]">
                                    <span className="text-gray-600 font-medium">Condition:</span>
                                    <select
                                      value={condition}
                                      onChange={(e) => setConditions(prev => ({ ...prev, [itemKey]: e.target.value }))}
                                      className="h-7 text-[10px] rounded border bg-white px-1 py-0.5 w-[90px]"
                                    >
                                      <option value="excellent">Excellent</option>
                                      <option value="good">Good</option>
                                      <option value="fair">Fair</option>
                                      <option value="poor">Poor</option>
                                      <option value="damaged">Damaged</option>
                                    </select>
                                  </div>
                                )}

                                <div className="flex items-center justify-between pt-1">
                                  <button
                                    type="button"
                                    onClick={() => setPhotos(prev => ({ ...prev, [itemKey]: !prev[itemKey] }))}
                                    className={cn(
                                      "flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border transition-colors",
                                      hasPhoto ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-gray-600",
                                      isDiscrepancy && requirePhotosOnMismatch && !hasPhoto && "border-rose-400 bg-rose-50 text-rose-700"
                                    )}
                                  >
                                    <Camera className="h-3.5 w-3.5" />
                                    <span>{hasPhoto ? "Photo Attached" : "Attach Photo"}</span>
                                  </button>
                                  {isDiscrepancy && (
                                    <span className="text-[8px] text-amber-600 font-semibold italic bg-amber-50 px-1.5 py-0.5 rounded">
                                      {requirePhotosOnMismatch ? "Photo Required!" : "Count mismatch!"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <button 
                          type="button" 
                          disabled={!isCampProgressDone}
                          onClick={() => {
                            setCompletedCampaigns(prev => ({ ...prev, [`${selectedStoreId}-${selectedMobileCampaignId}`]: true }));
                            setSelectedMobileCampaignId(null);
                          }}
                          className={cn(
                            "w-full rounded-xl py-2.5 text-xs font-semibold text-white shadow-sm flex items-center justify-center gap-1 mt-4 mb-3 transition-all",
                            isCampProgressDone 
                              ? "opacity-100 active:scale-[0.98]" 
                              : "opacity-50 cursor-not-allowed"
                          )}
                          style={{ backgroundColor: '#0F3A3F' }}
                        >
                          <span>Submit Campaign Verification</span>
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : activeScreen === 'sign_off' ? (
              /* STAGE 3: MANAGER SIGN-OFF / CLOSURE SCREEN */
              <div className="min-h-0 flex-1 flex flex-col justify-between overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 space-y-3">
                  <div className="rounded-xl border border-green-100 bg-green-50/70 p-3 text-left">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      All Tasks Complete!
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-green-950 font-medium">
                      All campaigns at {selectedStore?.name} have been completed and verified.
                    </p>
                  </div>

                  <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3 text-left">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5" />
                      Next Step: Manager Sign-Off
                    </p>
                    <p className="mt-1 text-[10px] leading-normal text-blue-900">
                      A manager must sign off on this store visit to confirm completion and lock it from further edits.
                    </p>
                  </div>

                  {/* Sign-off Method Row */}
                  <div className="space-y-1.5 text-left">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Sign-off Method</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSignoffMethod(prev => ({ ...prev, [selectedStoreId!]: 'signature' }))}
                        className={cn(
                          "flex-1 text-[10px] font-semibold border py-2 rounded-lg transition-colors",
                          (signoffMethod[selectedStoreId!] ?? 'signature') === 'signature'
                            ? "bg-sky-50 text-sky-700 border-sky-300 font-bold"
                            : "bg-white hover:bg-slate-50"
                        )}
                      >
                        Sign
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignoffMethod(prev => ({ ...prev, [selectedStoreId!]: 'typed_name' }))}
                        className={cn(
                          "flex-1 text-[10px] font-semibold border py-2 rounded-lg transition-colors",
                          signoffMethod[selectedStoreId!] === 'typed_name'
                            ? "bg-sky-50 text-sky-700 border-sky-300 font-bold"
                            : "bg-white hover:bg-slate-50"
                        )}
                      >
                        Enter name
                      </button>
                    </div>
                  </div>

                  {/* Signature Pad / Manager Name Section */}
                  <div className="rounded-xl border bg-white p-3 shadow-sm space-y-2 text-left">
                    {(signoffMethod[selectedStoreId!] ?? 'signature') === 'signature' ? (
                      <>
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Manager Signature *</p>
                        {signatureData[selectedStoreId!] ? (
                          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg py-3 px-2 flex flex-col items-center gap-1.5">
                            <span className="text-[10px] font-bold text-emerald-700">✓ Signature Captured</span>
                            <button
                              type="button"
                              onClick={() => setSignatureData(prev => ({ ...prev, [selectedStoreId!]: false }))}
                              className="text-[9px] font-bold border border-emerald-300 bg-white text-emerald-800 px-2 py-0.5 rounded"
                            >
                              Clear & Re-sign
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setSignatureData(prev => ({ ...prev, [selectedStoreId!]: true }))}
                            className="w-full bg-blue-50 border-2 border-dashed border-blue-300 hover:border-blue-400 rounded-lg py-5 text-center flex flex-col items-center justify-center cursor-pointer transition-colors"
                          >
                            <span className="text-[11px] font-semibold text-blue-700">Tap to draw signature</span>
                            <span className="text-[8px] text-gray-400 mt-2">X _______________________</span>
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Store manager name *</p>
                        <input
                          type="text"
                          value={managerName[selectedStoreId!] ?? ''}
                          onChange={(e) => setManagerName(prev => ({ ...prev, [selectedStoreId!]: e.target.value }))}
                          className="w-full text-xs border rounded p-2 bg-slate-50 focus:bg-white"
                          placeholder="Enter manager name"
                        />
                      </>
                    )}
                  </div>

                  <div className="rounded-xl border bg-white p-3 shadow-sm space-y-2 text-left">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Closing Comments</p>
                    <textarea
                      rows={2}
                      value={closingRemarks[selectedStoreId!] ?? ''}
                      onChange={(e) => setClosingRemarks((prev) => ({ ...prev, [selectedStoreId!]: e.target.value }))}
                      className="w-full text-xs rounded border p-2 bg-slate-50 focus:bg-white resize-none"
                      placeholder="Type store audit summary notes..."
                    />
                  </div>

                  {closingSections.map((section) => (
                    <div key={section.id} className="rounded-xl border bg-white p-3 shadow-sm space-y-2 text-left">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{section.title}</p>
                      <div className="space-y-2.5">
                        {section.questions.map((question) => {
                          const answerKey = `${selectedStoreId}-${question.id}`;
                          const answer = closureAnswers[answerKey];

                          return (
                            <div key={question.id} className="space-y-1">
                              <p className="text-[11px] font-medium text-gray-800">
                                {question.label} {question.required && <span className="text-red-500">*</span>}
                              </p>
                              {question.type === 'yes_no' ? (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setClosureAnswers((prev) => ({ ...prev, [answerKey]: 'yes' }))}
                                    className={cn(
                                      "text-[10px] border px-2.5 py-1 rounded transition-colors",
                                      answer === 'yes'
                                        ? "bg-slate-900 text-white font-semibold"
                                        : "bg-slate-50 hover:bg-slate-100"
                                    )}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setClosureAnswers((prev) => ({ ...prev, [answerKey]: 'no' }))}
                                    className={cn(
                                      "text-[10px] border px-2.5 py-1 rounded transition-colors",
                                      answer === 'no'
                                        ? "bg-slate-900 text-white font-semibold"
                                        : "bg-slate-50 hover:bg-slate-100"
                                    )}
                                  >
                                    No
                                  </button>
                                </div>
                              ) : question.type === 'number' ? (
                                <input
                                  type="number"
                                  value={answer ?? ''}
                                  onChange={(e) => setClosureAnswers((prev) => ({ ...prev, [answerKey]: e.target.value }))}
                                  className="w-full text-[11px] border p-1.5 rounded bg-slate-50"
                                  placeholder="Enter number..."
                                />
                              ) : question.type === 'photo' ? (
                                <button
                                  type="button"
                                  onClick={() => setClosureAnswers((prev) => ({ ...prev, [answerKey]: !prev[answerKey] }))}
                                  className={cn(
                                    "flex items-center gap-1 text-[10px] font-medium px-2 py-1.5 rounded border w-full justify-center",
                                    answer ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-gray-600"
                                  )}
                                >
                                  <Camera className="h-3.5 w-3.5" />
                                  <span>{answer ? "Photo Attached" : "Attach Closure Photo"}</span>
                                </button>
                              ) : (
                                <input
                                  type="text"
                                  value={answer ?? ''}
                                  onChange={(e) => setClosureAnswers((prev) => ({ ...prev, [answerKey]: e.target.value }))}
                                  className="w-full text-[11px] border p-1.5 rounded bg-slate-50 focus:bg-white"
                                  placeholder="Type answer..."
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Submit button checks constraints */}
                {(() => {
                  const method = signoffMethod[selectedStoreId!] ?? 'signature';
                  const isSignedOk = method === 'signature' 
                    ? signatureData[selectedStoreId!] === true 
                    : !!(managerName[selectedStoreId!] ?? '').trim();

                  return (
                    <button 
                      type="button" 
                      disabled={!isSignedOk}
                      onClick={() => {
                        setCompletedStores(prev => ({ ...prev, [selectedStoreId as string]: true }));
                        setShowSignOffModal(false);
                      }}
                      className={cn(
                        "w-full rounded-xl py-2.5 text-xs font-semibold text-white shadow-md flex items-center justify-center gap-1.5 mb-3 transition-all",
                        isSignedOk ? "opacity-100 active:scale-[0.98]" : "opacity-50 cursor-not-allowed"
                      )}
                      style={{ backgroundColor: '#0F3A3F' }}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Sign Off & Lock Store</span>
                    </button>
                  );
                })()}
              </div>
            ) : (
              /* STORE DETAILS / TASK LIST SCREEN */
              <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 space-y-3 animate-in fade-in">
                {/* Store Info Profile */}
                <div className="rounded-xl border bg-white p-3 shadow-sm text-left">
                  <p className="text-xs font-semibold text-gray-950">
                    {selectedStore?.name}
                  </p>
                  <p className="text-[9px] text-gray-500 mt-0.5 font-medium">
                    {selectedStore?.address}
                  </p>
                </div>

                {/* Progress Summary Card */}
                <div className="grid grid-cols-3 bg-white border rounded-xl p-3 text-center shadow-sm">
                  <div className="border-r">
                    <p className="text-[8px] font-bold text-gray-400 uppercase">Completed</p>
                    <p className="text-xs font-extrabold text-teal-800 mt-0.5">
                      {storeCampaignsCompleted} / {expectedCampaigns.length}
                    </p>
                  </div>
                  <div className="border-r">
                    <p className="text-[8px] font-bold text-gray-400 uppercase">Outstanding</p>
                    <p className="text-xs font-extrabold text-rose-500 mt-0.5">
                      {expectedCampaigns.length - storeCampaignsCompleted}
                    </p>
                  </div>
                  <div>
                    <p className="text-[8px] font-bold text-gray-400 uppercase">Progress</p>
                    <p className="text-xs font-extrabold text-emerald-600 mt-0.5">
                      {expectedCampaigns.length > 0 
                        ? Math.round((storeCampaignsCompleted / expectedCampaigns.length) * 100)
                        : 0}%
                    </p>
                  </div>
                </div>

                {/* Store Status Signature Banners */}
                {isStoreAlreadySigned ? (
                  <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-center shadow-sm space-y-1">
                    <p className="text-xs font-bold text-green-800">Store Sign-Off Complete</p>
                    <p className="text-[9px] text-green-700">Manager signature recorded successfully.</p>
                  </div>
                ) : isStoreReadyForSignature ? (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center shadow-sm space-y-2">
                    <p className="text-xs font-bold text-blue-800">All Verification Tasks Done</p>
                    <p className="text-[9px] text-blue-700">This store is ready for manager sign-off.</p>
                    <button
                      type="button"
                      onClick={() => setShowSignOffModal(true)}
                      className="w-full rounded-lg py-1.5 text-xs font-bold text-white shadow-sm flex items-center justify-center gap-1.5 transition-transform active:scale-[0.98]"
                      style={{ backgroundColor: '#0F3A3F' }}
                    >
                      <span>Proceed to Sign-Off</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center shadow-sm">
                    <p className="text-xs font-bold text-slate-700">In Progress</p>
                    <p className="text-[9px] text-slate-500 mt-0.5">Verify all active campaign counts to unlock sign-off.</p>
                  </div>
                )}

                {/* Tasks List Header */}
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 text-left">
                  Verification Tasks
                </p>

                {/* Verification tasks list */}
                <div className="space-y-2.5">
                  {expectedCampaigns.map((camp) => {
                    const isCampDone = completedCampaigns[`${selectedStoreId}-${camp.id}`] === true;

                    return (
                      <div
                        key={camp.id}
                        className={cn(
                          "rounded-xl border p-3 bg-white shadow-sm flex justify-between items-center gap-3 transition-all",
                          isCampDone && "bg-emerald-50/20 border-emerald-100"
                        )}
                      >
                        <div className="min-w-0 flex-1 text-left">
                          <Badge className={cn(
                            "text-[8px] font-bold py-0 px-1 hover:bg-slate-50 border capitalize",
                            isCampDone 
                              ? "bg-green-50 border-green-200 text-green-700" 
                              : "bg-blue-50 border-blue-200 text-blue-700"
                          )}>
                            {isCampDone ? 'Completed' : 'Pending'}
                          </Badge>
                          <p className="mt-1.5 text-xs font-semibold text-slate-900 truncate">
                            {camp.name}
                          </p>
                          <p className="text-[9px] text-slate-500 mt-0.5">
                            {camp.tag} · {camp.items.length} units
                          </p>
                        </div>

                        <div className="shrink-0">
                          {isCampDone ? (
                            <button
                              type="button"
                              onClick={() => setSelectedMobileCampaignId(camp.id)}
                              className="text-[10px] font-bold border px-2.5 py-1 rounded bg-slate-50 hover:bg-slate-100 transition-colors"
                            >
                              View
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedMobileCampaignId(camp.id)}
                              className="text-[10px] font-bold text-white px-2.5 py-1 rounded transition-all active:scale-[0.97]"
                              style={{ backgroundColor: '#0F3A3F' }}
                            >
                              Start
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Manager Sign-Off Card Accordion */}
                <div className="rounded-xl border bg-white shadow-sm overflow-hidden text-left mt-2">
                  <button
                    type="button"
                    onClick={() => setSignOffExpanded(v => !v)}
                    className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 transition-colors border-b"
                  >
                    <span className="text-xs font-semibold text-slate-900">
                      {isStoreAlreadySigned ? 'Signed: ' : ''}Store Manager Sign-Off
                    </span>
                    <span className="text-[9px] font-bold text-slate-400">
                      {signOffExpanded ? '▲' : '▼'}
                    </span>
                  </button>
                  {signOffExpanded && (
                    <div className="p-3 bg-slate-50/50 space-y-2">
                      <p className="text-[9px] text-slate-500 leading-normal">
                        {isStoreAlreadySigned
                          ? 'Signature on file'
                          : isStoreReadyForSignature
                            ? 'Awaiting manager signature'
                            : 'Complete all tasks to unlock manager signature.'}
                      </p>
                      <button
                        type="button"
                        disabled={!isStoreReadyForSignature || Boolean(isStoreAlreadySigned)}
                        onClick={() => setShowSignOffModal(true)}
                        className={cn(
                          "w-full py-2 rounded-lg text-xs font-semibold text-white shadow-sm flex items-center justify-center transition-all",
                          isStoreReadyForSignature && !isStoreAlreadySigned
                            ? "bg-teal-700 active:scale-[0.97] cursor-pointer"
                            : "bg-slate-300 opacity-60 cursor-not-allowed"
                        )}
                      >
                        {isStoreAlreadySigned ? 'Signed' : 'Capture Signature'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-1 w-16 rounded-full bg-gray-400" />
    </div>
  );
}

function formatDate(value: string | null) {
  if (!value) return 'Not set';
  return new Intl.DateTimeFormat('en-IE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

function formatDateRange(startDate: string | null, endDate: string | null) {
  if (!startDate && !endDate) return 'Dates not set';
  if (!startDate) return `Ends ${formatDate(endDate)}`;
  if (!endDate) return `Starts ${formatDate(startDate)}`;
  return `${formatDate(startDate)} to ${formatDate(endDate)}`;
}

function formatPercent(value: number | null) {
  return value === null ? 'Not scored' : `${value}%`;
}

function formatAuditSubmissionStatus(value: AuditStoreCampaignResult['submissionStatus']) {
  switch (value) {
    case 'approved':
      return 'Submitted and approved';
    case 'submitted':
      return 'Submitted';
    case 'rejected':
      return 'Submitted and rejected';
    case 'not_submitted':
    default:
      return 'Not submitted';
  }
}

function formatAuditStoreStatus(value: AuditCampaignStore['resultStatus']) {
  switch (value) {
    case 'reviewed':
      return 'Reviewed';
    case 'submitted':
      return 'Submitted';
    case 'in_progress':
      return 'In progress';
    case 'not_started':
    default:
      return 'Not started';
  }
}

export default function AdminAuditsPage() {
  const [location, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const showCreateWizard = location === '/admin/audits/new';
  const showTemplates = location === '/admin/audits/templates';
  const auditDetailMatch = location.match(/^\/admin\/audits\/(\d+)$/);
  const auditDetailId = auditDetailMatch ? Number(auditDetailMatch[1]) : null;
  const showAuditDetail = Number.isInteger(auditDetailId) && auditDetailId !== null && auditDetailId > 0;
  const [currentStep, setCurrentStep] = useState(1);
  const [draftName, setDraftName] = useState('');
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>('');
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [auditStartDate, setAuditStartDate] = useState(getTodayInputDate);
  const [auditType, setAuditType] = useState<AuditType>('full');
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedStores, setSelectedStores] = useState<AuditStore[]>([]);
  const [storeAuditorIds, setStoreAuditorIds] = useState<Record<number, string>>({});
  const [selectedAuditStoreId, setSelectedAuditStoreId] = useState<number | null>(null);
  const [selectedAuditResult, setSelectedAuditResult] = useState<AuditStoreCampaignResult | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateDraft, setTemplateDraft] = useState<AuditTemplateDraft | null>(null);
  const [templateTab, setTemplateTab] = useState<'briefing' | 'rules' | 'closure'>('briefing');

  const [briefingOverrides, setBriefingOverrides] = useState<Record<number, boolean>>({});
  const [activeDetailTab, setActiveDetailTab] = useState<'stores' | 'campaigns' | 'auditors'>('stores');
  const [selectedCampaignKey, setSelectedCampaignKey] = useState<string | null>(null);
  const [selectedAuditorId, setSelectedAuditorId] = useState<number | null>(null);

  const [activeLandingTab, setActiveLandingTab] = useState<'audits' | 'stores' | 'campaigns' | 'auditors'>('audits');
  // Lets admins quickly separate built-but-unsent drafts from live/completed audits.
  const [auditStatusFilter, setAuditStatusFilter] = useState<'all' | 'draft' | 'sent' | 'completed' | 'archived'>('all');
  const [showArchivedTemplates, setShowArchivedTemplates] = useState(false);
  const [landingSelectedCampaignKey, setLandingSelectedCampaignKey] = useState<string | null>(null);
  const [landingSelectedAuditorId, setLandingSelectedAuditorId] = useState<number | null>(null);

  useEffect(() => {
    setActiveDetailTab('stores');
    setSelectedCampaignKey(null);
    setSelectedAuditorId(null);
  }, [auditDetailId]);

  // Reset the create-audit wizard whenever it is (re)opened so a previously
  // submitted route can't be accidentally re-created from stale selections.
  useEffect(() => {
    if (!showCreateWizard) return;
    setCurrentStep(1);
    setDraftName('');
    setAuditType('full');
    setAuditStartDate(getTodayInputDate());
    setStoreSearch('');
    setSelectedStores([]);
    setStoreAuditorIds({});
  }, [showCreateWizard]);

  const closingSections = templateDraft?.sections.filter(
    (s) => s.id !== 'briefing' && s.phase !== 'briefing' && s.id !== 'core-rules'
  ) ?? [];

  const overviewQuery = useQuery<AdminAuditsOverview>({
    queryKey: ['/api/admin/audits/overview'],
    queryFn: getQueryFn({ on401: 'throw' }),
  });

  const overview = overviewQuery.data;
  const retailers = useMemo(() => {
    const map = new Map<number, { id: number; name: string; logo: string | null; lockedCycleCount: number }>();
    for (const cycle of overview?.candidateCycles ?? []) {
      const current = map.get(cycle.retailerId);
      map.set(cycle.retailerId, {
        id: cycle.retailerId,
        name: cycle.retailerName,
        logo: current?.logo ?? cycle.retailerLogo ?? null,
        lockedCycleCount: (current?.lockedCycleCount ?? 0) + 1,
      });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [overview?.candidateCycles]);

  useEffect(() => {
    if (!selectedRetailerId && retailers.length > 0) {
      setSelectedRetailerId(String(retailers[0].id));
    }
  }, [retailers, selectedRetailerId]);

  const filteredCycles = useMemo(() => {
    const all = overview?.candidateCycles ?? [];
    const scoped = selectedRetailerId
      ? all.filter((cycle) => String(cycle.retailerId) === selectedRetailerId)
      : all;

    // Defensive: collapse to one row per cycle (keep the most recently locked) so
    // selection — which is keyed by cycleId — is always unambiguous.
    const byCycle = new Map<number, AuditCandidateCycle>();
    for (const cycle of scoped) {
      const existing = byCycle.get(cycle.cycleId);
      const lockedAt = cycle.lockedAt ? new Date(cycle.lockedAt).getTime() : 0;
      const existingLockedAt = existing?.lockedAt ? new Date(existing.lockedAt).getTime() : -1;
      if (!existing || lockedAt > existingLockedAt) {
        byCycle.set(cycle.cycleId, cycle);
      }
    }

    // Newest to oldest by cycle start date, falling back to lock time.
    return Array.from(byCycle.values()).sort((a, b) => {
      const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
      if (bStart !== aStart) return bStart - aStart;
      const aLocked = a.lockedAt ? new Date(a.lockedAt).getTime() : 0;
      const bLocked = b.lockedAt ? new Date(b.lockedAt).getTime() : 0;
      return bLocked - aLocked;
    });
  }, [overview?.candidateCycles, selectedRetailerId]);

  const selectedCycle = useMemo(() => {
    if (!filteredCycles.length) return null;
    const explicit = filteredCycles.find((cycle) => String(cycle.cycleId) === selectedCycleId);
    return explicit ?? filteredCycles[0];
  }, [filteredCycles, selectedCycleId]);

  useEffect(() => {
    if (selectedCycle && String(selectedCycle.cycleId) !== selectedCycleId) {
      setSelectedCycleId(String(selectedCycle.cycleId));
    }
  }, [selectedCycle, selectedCycleId]);

  useEffect(() => {
    setSelectedStores([]);
    setStoreAuditorIds({});
    setStoreSearch('');
  }, [selectedCycle?.cycleId, auditType]);

  const storeSearchParams = new URLSearchParams({ auditType });
  if (storeSearch.trim()) storeSearchParams.set('search', storeSearch.trim());
  const storeSearchPath = selectedCycle?.cycleId
    ? `/api/admin/audits/cycles/${selectedCycle.cycleId}/stores?${storeSearchParams.toString()}`
    : '';

  const storesQuery = useQuery<CycleStoresResponse>({
    queryKey: [storeSearchPath],
    queryFn: getQueryFn({ on401: 'throw' }),
    enabled: !!selectedCycle?.cycleId,
  });

  const auditorsQuery = useQuery<AuditorsResponse>({
    queryKey: ['/api/admin/audits/auditors'],
    queryFn: getQueryFn({ on401: 'throw' }),
  });

  const templatesQuery = useQuery<AuditTemplatesResponse>({
    queryKey: ['/api/admin/audits/templates'],
    queryFn: getQueryFn({ on401: 'throw' }),
    enabled: showTemplates,
  });

  const auditDetailQuery = useQuery<AuditDetailResponse>({
    queryKey: [`/api/admin/audits/${auditDetailId}`],
    queryFn: getQueryFn({ on401: 'throw' }),
    enabled: showAuditDetail,
  });

  const cycleStores = storesQuery.data?.stores ?? [];
  const auditors = auditorsQuery.data?.auditors ?? [];
  const auditTemplates = templatesQuery.data?.templates ?? [];
  const assignedStoreCount = selectedStores.filter((store) => storeAuditorIds[store.id]).length;
  const conflictCount = selectedStores.filter((store) => {
    const auditorId = Number(storeAuditorIds[store.id]);
    return Number.isInteger(auditorId) && store.originalOperators.some((operator) => operator.id === auditorId);
  }).length;
  const auditCampaigns = overview?.auditCampaigns ?? [];
  const auditDetail = auditDetailQuery.data;

  const selectedAuditStore = useMemo(() => {
    if (!auditDetail?.stores) return null;
    return auditDetail.stores.find((store) => store.id === selectedAuditStoreId) ?? auditDetail.stores[0] ?? null;
  }, [auditDetail?.stores, selectedAuditStoreId]);

  const dialogStore = useMemo(() => {
    if (!selectedAuditResult || !auditDetail?.stores) return null;
    const sId = selectedAuditResult.storeId;
    return auditDetail.stores.find(s => s.storeId === sId || s.id === sId) ?? selectedAuditStore;
  }, [selectedAuditResult, auditDetail?.stores, selectedAuditStore]);

  const selectedGroupResults = useMemo(() => {
    if (!selectedAuditResult || !dialogStore) return [];
    const groupKey = `${selectedAuditResult.campaignId ?? selectedAuditResult.campaignName}-${selectedAuditResult.taskName ?? ''}`;
    return dialogStore.campaignResults.filter(
      (r) => `${r.campaignId ?? r.campaignName}-${r.taskName ?? ''}` === groupKey
    );
  }, [selectedAuditResult, dialogStore]);

  // Landing page column definitions
  const archiveAuditMutation = useMutation({
    mutationFn: async ({ auditId, archive }: { auditId: number; archive: boolean }) => {
      await apiRequest(`/api/admin/audits/${auditId}/archive`, 'POST', { archive });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/overview'] });
      toast({
        title: variables.archive ? 'Audit archived' : 'Audit restored',
        description: variables.archive
          ? 'The audit is hidden from the active list.'
          : 'The audit is active again.',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not update audit', description: error.message, variant: 'destructive' });
    },
  });

  const auditsColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Audit
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const audit = row.original;
        return (
          <div>
            <button
              type="button"
              className="text-left font-medium text-gray-900 hover:text-primary hover:underline"
              onClick={() => navigate(`/admin/audits/${audit.id}`)}
            >
              {audit.name}
            </button>
            <p className="text-xs text-gray-500">{AUDIT_TYPE_LABELS[audit.auditType as AuditType]}</p>
          </div>
        );
      }
    },
    {
      accessorKey: 'retailerName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Retailer
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.retailerName,
    },
    {
      accessorKey: 'cycleName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Cycle
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const audit = row.original;
        return (
          <div>
            <p>{audit.cycleName}</p>
            <p className="text-xs text-gray-500">{formatDate(audit.startDate)} to {formatDate(audit.endDate)}</p>
          </div>
        );
      }
    },
    {
      accessorKey: 'auditStartDate',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Start date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => formatDate(row.original.auditStartDate),
    },
    {
      accessorKey: 'storeCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Stores
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.storeCount}</div>,
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.findingCount}</div>,
    },
    {
      accessorKey: 'complianceScore',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{formatPercent(row.original.complianceScore)}</div>,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'planned' ? 'secondary' : 'outline'}>
          {row.original.status.replaceAll('_', ' ')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => {
        const audit = row.original;
        const isArchived = Boolean(audit.archivedAt);
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/audits/${audit.id}`)}
            >
              <Eye className="mr-2 h-4 w-4" />
              View Audit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={isArchived ? 'text-primary' : 'text-muted-foreground'}
              disabled={archiveAuditMutation.isPending}
              title={isArchived ? 'Restore audit' : 'Archive audit'}
              onClick={() => {
                if (isArchived) {
                  archiveAuditMutation.mutate({ auditId: audit.id, archive: false });
                } else if (window.confirm(`Archive "${audit.name}"? It will be hidden from the active list but not deleted.`)) {
                  archiveAuditMutation.mutate({ auditId: audit.id, archive: true });
                }
              }}
            >
              {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
          </div>
        );
      },
    }
  ], [navigate, archiveAuditMutation]);

  const storesColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'storeName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Store
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-semibold text-gray-900">{row.original.storeName}</div>,
    },
    {
      accessorKey: 'auditName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Audit / Retailer
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const store = row.original;
        return (
          <div>
            <p className="font-medium text-gray-800">{store.auditName}</p>
            <p className="text-xs text-muted-foreground">{store.retailerName}</p>
          </div>
        );
      }
    },
    {
      accessorKey: 'auditorName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Auditor
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.auditorName,
    },
    {
      accessorKey: 'expectedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Expected Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedQuantity}</div>,
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.findingCount}</div>,
    },
    {
      accessorKey: 'complianceScore',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Compliance Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const val = row.original.complianceScore;
        return <div className="text-right font-medium">{val === null ? '-' : `${val}%`}</div>;
      }
    },
    {
      accessorKey: 'responseStatus',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const status = row.original.responseStatus;
        return (
          <div>
            <Badge variant={
              status === 'submitted' || status === 'reviewed' ? 'default' :
              status === 'in_progress' ? 'secondary' : 'outline'
            }>
              {status.replaceAll('_', ' ')}
            </Badge>
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/audits/${row.original.auditId}`)}
          >
            View Audit Detail
          </Button>
        </div>
      ),
    }
  ], [navigate]);

  const campaignsColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'campaignName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Campaign
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <p className="font-semibold text-gray-900">{row.original.campaignName}</p>
          {row.original.auditName && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Audit: {row.original.auditName} / Cycle: {row.original.cycleName || 'N/A'}
            </p>
          )}
        </div>
      ),
    },
    {
      id: 'storesCovered',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Stores Covered
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.storeIds.size}</div>,
    },
    {
      accessorKey: 'expectedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Expected Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedQuantity}</div>,
    },
    {
      accessorKey: 'approvedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Approved Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.approvedQuantity}</div>,
    },
    {
      accessorKey: 'submittedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Submitted Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.submittedQuantity}</div>,
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right font-medium text-red-600">{row.original.findingCount}</div>,
    },
    {
      id: 'status',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const camp = row.original;
        return (
          <Badge variant={camp.verifiedCount === camp.storeIds.size ? 'default' : 'secondary'}>
            {camp.verifiedCount} / {camp.storeIds.size} stores
          </Badge>
        );
      }
    }
  ], []);

  const auditorsColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'auditorName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Auditor
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-semibold text-gray-900">{row.original.auditorName}</div>,
    },
    {
      accessorKey: 'completedStores',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Completed Stores
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right font-medium">{row.original.completedStores}</div>,
    },
    {
      accessorKey: 'totalStores',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Total Assigned
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.totalStores}</div>,
    },
    {
      accessorKey: 'expectedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Expected Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedQuantity}</div>,
    },
    {
      accessorKey: 'verifiedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Verified Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.verifiedQuantity}</div>,
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right font-medium text-red-600">{row.original.findingCount}</div>,
    }
  ], []);

  // Detail screen column definitions
  const detailStoresColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'storeName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Store
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const store = row.original;
        return (
          <div>
            <p className="font-medium text-gray-900">{store.storeName}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <MapPin className="h-3 w-3" />
              {store.storeAddress || store.storeRegion || 'No address'}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Original operator: {store.originalOperators.length > 0
                ? store.originalOperators.map((operator: any) => operator.name || `User ${operator.id}`).join(', ')
                : 'Unknown'}
            </p>
          </div>
        );
      }
    },
    {
      accessorKey: 'auditorName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Auditor
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.original.auditorName}</p>
          {row.original.auditorEmail && <p className="text-xs text-gray-500">{row.original.auditorEmail}</p>}
        </div>
      ),
    },
    {
      accessorKey: 'expectedCampaignCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Campaigns
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedCampaignCount}</div>,
    },
    {
      accessorKey: 'resultStatus',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Audit status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const store = row.original;
        return (
          <div className="flex flex-col gap-1">
            <Badge
              variant={store.resultStatus === 'not_started' ? 'outline' : 'secondary'}
              className={cn(store.resultStatus === 'not_started' && 'text-gray-600', 'w-fit')}
            >
              {formatAuditStoreStatus(store.resultStatus)}
            </Badge>
            {store.submittedAt ? (
              <p className="text-xs text-gray-500">
                Audit Date: {formatDate(store.submittedAt)}
              </p>
            ) : (
              <p className="text-xs text-gray-500">No audit response submitted yet</p>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.findingCount}</div>,
    },
    {
      accessorKey: 'complianceScore',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{formatPercent(row.original.complianceScore)}</div>,
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            type="button"
            variant={selectedAuditStoreId === row.original.id ? 'default' : 'outline'}
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedAuditStoreId(row.original.id);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Store
          </Button>
        </div>
      ),
    }
  ], [selectedAuditStoreId, setSelectedAuditStoreId]);

  const detailCampaignsColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'campaignName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Campaign
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <p className="font-semibold text-gray-900">{row.original.campaignName}</p>
          {row.original.campaignId && <p className="text-xs text-gray-500">ID #{row.original.campaignId}</p>}
        </div>
      ),
    },
    {
      id: 'storesCoverage',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Stores Coverage
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.storeIds.size} store{row.original.storeIds.size === 1 ? '' : 's'}</div>,
    },
    {
      accessorKey: 'approvedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Approved Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.approvedQuantity}</div>,
    },
    {
      accessorKey: 'submittedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Verified Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.verifiedCount === 0 ? (
            <span className="text-xs text-muted-foreground italic">Pending</span>
          ) : (
            <span>{row.original.submittedQuantity}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.findingCount}</div>,
    },
    {
      id: 'score',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const camp = row.original;
        const score = camp.verifiedCount === 0
          ? null
          : Math.round(((camp.verifiedCount - camp.findingCount) / camp.verifiedCount) * 100);
        return (
          <div className="text-right font-semibold">
            {score !== null ? formatPercent(score) : <span className="text-gray-400">—</span>}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            type="button"
            variant={selectedCampaignKey === row.original.campaignName ? 'default' : 'outline'}
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedCampaignKey(row.original.campaignName);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Campaign
          </Button>
        </div>
      ),
    }
  ], [selectedCampaignKey, setSelectedCampaignKey]);

  const detailAuditorsColumns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: 'auditorName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Auditor
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-semibold text-gray-900">{row.original.auditorName}</div>,
    },
    {
      accessorKey: 'expectedStores',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Stores Assigned
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedStores}</div>,
    },
    {
      id: 'progress',
      header: () => <div>Status / Progress</div>,
      cell: ({ row }) => {
        const aud = row.original;
        return (
          <div className="flex flex-wrap gap-1 text-[10px]">
            <Badge variant="default" className="bg-emerald-50 text-emerald-800 border-emerald-200">
              {aud.completedStores} done
            </Badge>
            {aud.inProgressStores > 0 && (
              <Badge variant="secondary">
                {aud.inProgressStores} in progress
              </Badge>
            )}
            {aud.notStartedStores > 0 && (
              <Badge variant="outline">
                {aud.notStartedStores} pending
              </Badge>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'expectedQuantity',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Approved Qty
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right">{row.original.expectedQuantity}</div>,
    },
    {
      accessorKey: 'findingCount',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Findings
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => <div className="text-right font-medium text-red-600">{row.original.findingCount}</div>,
    },
    {
      id: 'avgScore',
      header: ({ column }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8"
          >
            Avg Score
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
      cell: ({ row }) => {
        const aud = row.original;
        const avgScore = aud.totalScored === 0
          ? null
          : Math.round(((aud.totalScored - aud.findingCount) / aud.totalScored) * 100);
        return (
          <div className="text-right font-semibold">
            {avgScore !== null ? formatPercent(avgScore) : <span className="text-gray-400">—</span>}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            type="button"
            variant={selectedAuditorId === row.original.auditorId ? 'default' : 'outline'}
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedAuditorId(row.original.auditorId);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View Auditor
          </Button>
        </div>
      ),
    }
  ], [selectedAuditorId, setSelectedAuditorId]);

  useEffect(() => {
    if (!showAuditDetail) {
      setSelectedAuditStoreId(null);
      return;
    }

    const stores = auditDetail?.stores ?? [];
    if (stores.length === 0) {
      setSelectedAuditStoreId(null);
      return;
    }

    if (!selectedAuditStoreId || !stores.some((store) => store.id === selectedAuditStoreId)) {
      setSelectedAuditStoreId(stores[0].id);
    }
  }, [auditDetail?.stores, selectedAuditStoreId, showAuditDetail]);

  useEffect(() => {
    if (!showTemplates || templateDraft || auditTemplates.length === 0) return;

    const firstTemplate = auditTemplates.find((t) => !t.archivedAt) ?? auditTemplates[0];
    setSelectedTemplateId(firstTemplate.id);
    setTemplateDraft(cloneTemplate(firstTemplate));
  }, [auditTemplates, showTemplates, templateDraft]);

  const auditSteps: Step[] = [
    { title: 'Setup', description: 'Audit basics' },
    { title: 'Retailer', description: 'Choose retailer' },
    { title: 'Cycle', description: 'Locked cycle' },
    { title: 'Stores', description: 'Pick stores' },
    { title: 'Auditors', description: 'Assign per store' },
    { title: 'Review', description: 'Check & create' },
  ];

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Audit Setup';
      case 2: return 'Choose Retailer';
      case 3: return 'Select Locked Cycle';
      case 4: return 'Select Stores';
      case 5: return 'Assign Auditors';
      case 6: return 'Review Audit Campaign';
      default: return 'Audit Setup';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!auditStartDate;
      case 2:
        return !!selectedRetailerId;
      case 3:
        return !!selectedCycle;
      case 4:
        return selectedStores.length > 0;
      case 5:
        return selectedStores.length > 0 && assignedStoreCount === selectedStores.length && conflictCount === 0;
      case 6:
        return selectedStores.length > 0 && assignedStoreCount === selectedStores.length && conflictCount === 0;
      default:
        return false;
    }
  };

  const nextStep = () => setCurrentStep((step) => Math.min(6, step + 1));
  const prevStep = () => setCurrentStep((step) => Math.max(1, step - 1));
  const handleStepClick = (stepIndex: number) => {
    const targetStep = stepIndex + 1;
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
    }
  };

  const auditCampaignName = draftName.trim() || (selectedCycle ? `${selectedCycle.retailerName} ${selectedCycle.cycleName} audit` : 'Cycle audit');

  const createAuditMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCycle) {
        throw new Error('Select a locked cycle before creating the audit');
      }

      const response = await apiRequest('/api/admin/audits', 'POST', {
        name: auditCampaignName,
        auditType,
        auditStartDate,
        retailerId: selectedCycle.retailerId,
        cycleId: selectedCycle.cycleId,
        stores: selectedStores.map((store) => ({
          storeId: store.id,
          auditorId: Number(storeAuditorIds[store.id]),
        })),
      });

      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/overview'] });
      toast({
        title: 'Saved as draft',
        description: 'Building is complete. Open the audit and use Send when you are ready to make it live.',
      });
      setSelectedStores([]);
      setStoreAuditorIds({});
      setDraftName('');
      setCurrentStep(1);
      // Building ends at "draft saved" — return to the list rather than jumping
      // straight to the send panel, keeping sending a separate deliberate step.
      navigate('/admin/audits');
    },
    onError: (error: Error) => {
      const data = (error as { data?: { error?: string; conflicts?: Array<{ storeName?: string }> } }).data;
      const conflictNames = (data?.conflicts ?? [])
        .map((c) => c.storeName)
        .filter(Boolean) as string[];
      const description = conflictNames.length
        ? `Auditors can't be assigned to stores they operate: ${conflictNames.join(', ')}`
        : data?.error || error.message;
      toast({
        title: 'Could not create audit',
        description,
        variant: 'destructive',
      });
    },
  });

  const sendDispatchMutation = useMutation({
    mutationFn: async (vars: { auditorId?: number; includeBriefing: boolean }) => {
      const response = await apiRequest(`/api/admin/audits/${auditDetailId}/send`, 'POST', {
        auditorId: vars.auditorId,
        includeBriefing: vars.includeBriefing,
      });
      return response.json();
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [`/api/admin/audits/${auditDetailId}`] }),
        queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/overview'] }),
      ]);
      toast({ title: 'Audit sent', description: 'The auditor can now see and start this audit.' });
    },
    onError: (error: Error) => {
      const data = (error as { data?: { error?: string } }).data;
      toast({ title: 'Could not send audit', description: data?.error || error.message, variant: 'destructive' });
    },
  });

  const recallDispatchMutation = useMutation({
    mutationFn: async (vars: { auditorId?: number }) => {
      const response = await apiRequest(`/api/admin/audits/${auditDetailId}/recall`, 'POST', {
        auditorId: vars.auditorId,
      });
      return response.json();
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [`/api/admin/audits/${auditDetailId}`] }),
        queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/overview'] }),
      ]);
      toast({ title: 'Audit recalled', description: 'The dispatch is hidden from the auditor again.' });
    },
    onError: (error: Error) => {
      const data = (error as { data?: { error?: string } }).data;
      toast({ title: 'Could not recall audit', description: data?.error || error.message, variant: 'destructive' });
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async (draft: AuditTemplateDraft) => {
      const payload = {
        name: draft.name,
        auditType: draft.auditType,
        openingText: draft.openingText,
        closingText: draft.closingText,
        sections: draft.sections,
      };
      const response = draft.id
        ? await apiRequest(`/api/admin/audits/templates/${draft.id}`, 'PUT', payload)
        : await apiRequest('/api/admin/audits/templates', 'POST', payload);
      return response.json();
    },
    onSuccess: async (data: { template: AuditTemplate }) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/templates'] });
      setSelectedTemplateId(data.template.id);
      setTemplateDraft(cloneTemplate(data.template));
      toast({ title: 'Template saved', description: `${data.template.name} has been updated.` });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not save template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      await apiRequest(`/api/admin/audits/templates/${templateId}`, 'DELETE');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/templates'] });
      setSelectedTemplateId(null);
      setTemplateDraft(null);
      toast({ title: 'Template deleted', description: 'The audit template has been removed.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not delete template',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: async ({ templateId, archive }: { templateId: number; archive: boolean }) => {
      await apiRequest(`/api/admin/audits/templates/${templateId}/archive`, 'POST', { archive });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/admin/audits/templates'] });
      toast({
        title: variables.archive ? 'Template archived' : 'Template restored',
        description: variables.archive
          ? 'The template is hidden from the active list.'
          : 'The template is active again.',
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not update template', description: error.message, variant: 'destructive' });
    },
  });

  const selectTemplate = (template: AuditTemplate) => {
    setSelectedTemplateId(template.id);
    setTemplateDraft(cloneTemplate(template));
  };

  const updateTemplateSection = (sectionId: string, changes: Partial<AuditTemplateSection>) => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: draft.sections.map((section) => (
            section.id === sectionId ? { ...section, ...changes } : section
          )),
        }
      : draft);
  };

  const updateTemplateQuestion = (sectionId: string, questionId: string, changes: Partial<AuditTemplateQuestion>) => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: draft.sections.map((section) => (
            section.id === sectionId
              ? {
                  ...section,
                  questions: section.questions.map((question) => (
                    question.id === questionId ? { ...question, ...changes } : question
                  )),
                }
              : section
          )),
        }
      : draft);
  };

  const addTemplateSection = () => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: [
            ...draft.sections,
            {
              id: makeDraftId('section'),
              title: 'New section',
              questions: [],
            },
          ],
        }
      : draft);
  };

  const removeTemplateSection = (sectionId: string) => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: draft.sections.filter((section) => section.id !== sectionId),
        }
      : draft);
  };

  const addTemplateQuestion = (sectionId: string) => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: draft.sections.map((section) => (
            section.id === sectionId
              ? {
                  ...section,
                  questions: [
                    ...section.questions,
                    {
                      id: makeDraftId('question'),
                      label: 'New question',
                      type: 'yes_no',
                      scope: 'per_store',
                      required: true,
                    },
                  ],
                }
              : section
          )),
        }
      : draft);
  };

  const removeTemplateQuestion = (sectionId: string, questionId: string) => {
    setTemplateDraft((draft) => draft
      ? {
          ...draft,
          sections: draft.sections.map((section) => (
            section.id === sectionId
              ? { ...section, questions: section.questions.filter((question) => question.id !== questionId) }
              : section
          )),
        }
      : draft);
  };

  if (showAuditDetail) {
    const audit = auditDetail?.audit;
    const stores = auditDetail?.stores ?? [];
    const dispatches = auditDetail?.dispatches ?? [];
    const hasUnsentDispatch = dispatches.some((d) => d.status === 'draft' || d.status === 'recalled');
    const dispatchActionPending = sendDispatchMutation.isPending || recallDispatchMutation.isPending;

    // Group campaign results across all stores
    const campaignMap = new Map<string, {
      campaignId: number | null;
      campaignName: string;
      expectedQuantity: number;
      approvedQuantity: number;
      submittedQuantity: number;
      findingCount: number;
      verifiedCount: number;
      storeIds: Set<number>;
      lines: Array<{
        storeName: string;
        auditorName: string;
        formatName: string;
        approvedQuantity: number;
        submittedQuantity: number | null;
        condition: string | null;
        comments: string;
        result: any;
      }>;
    }>();

    for (const store of stores) {
      for (const res of store.campaignResults) {
        const key = `${res.campaignId ?? res.campaignName}`;
        const existing = campaignMap.get(key) ?? {
          campaignId: res.campaignId,
          campaignName: res.campaignName,
          expectedQuantity: 0,
          approvedQuantity: 0,
          submittedQuantity: 0,
          findingCount: 0,
          verifiedCount: 0,
          storeIds: new Set<number>(),
          lines: [],
        };

        existing.expectedQuantity += res.expectedQuantity;
        existing.approvedQuantity += res.approvedQuantity;
        if (res.submittedQuantity !== null) {
          existing.submittedQuantity += res.submittedQuantity;
          existing.verifiedCount++;
          if (res.submittedQuantity !== res.approvedQuantity || res.condition === 'damaged') {
            existing.findingCount++;
          }
        }
        existing.storeIds.add(store.storeId);
        existing.lines.push({
          storeName: store.storeName,
          auditorName: store.auditorName,
          formatName: res.formatName,
          approvedQuantity: res.approvedQuantity,
          submittedQuantity: res.submittedQuantity,
          condition: res.condition,
          comments: res.comments,
          result: res,
        });

        campaignMap.set(key, existing);
      }
    }

    const campaignsList = Array.from(campaignMap.values()).sort((a, b) => a.campaignName.localeCompare(b.campaignName));

    // Group stores by auditor
    const auditorMap = new Map<number, {
      auditorId: number;
      auditorName: string;
      auditorEmail: string | null;
      expectedStores: number;
      completedStores: number;
      inProgressStores: number;
      notStartedStores: number;
      expectedQuantity: number;
      findingCount: number;
      totalScored: number;
      storesList: typeof stores;
    }>();

    for (const store of stores) {
      const existing = auditorMap.get(store.auditorId) ?? {
        auditorId: store.auditorId,
        auditorName: store.auditorName,
        auditorEmail: store.auditorEmail,
        expectedStores: 0,
        completedStores: 0,
        inProgressStores: 0,
        notStartedStores: 0,
        expectedQuantity: 0,
        findingCount: 0,
        totalScored: 0,
        storesList: [],
      };

      existing.expectedStores++;
      if (store.resultStatus === 'submitted' || store.resultStatus === 'reviewed') {
        existing.completedStores++;
      } else if (store.resultStatus === 'in_progress') {
        existing.inProgressStores++;
      } else {
        existing.notStartedStores++;
      }
      existing.expectedQuantity += store.expectedQuantity;
      existing.findingCount += store.findingCount;

      const verifiedCount = store.campaignResults.filter((result) => result.submittedQuantity !== null).length;
      const answerCount = store.auditResponse?.answerCount ?? 0;
      existing.totalScored += (verifiedCount + answerCount);

      existing.storesList.push(store);
      auditorMap.set(store.auditorId, existing);
    }

    const auditorsList = Array.from(auditorMap.values()).sort((a, b) => a.auditorName.localeCompare(b.auditorName));
    const totalApprovedQuantity = stores.reduce((sum, store) => sum + store.expectedQuantity, 0);
    const totalFindings = stores.reduce((sum, store) => sum + store.findingCount, 0);
    const selectedCampaignResults = selectedAuditStore?.campaignResults ?? [];
    const postingResults = selectedCampaignResults.filter((result) => result.auditType === 'Posting');
    const removalResults = selectedCampaignResults.filter((result) => result.auditType === 'Removal');
    const showPostingResults = audit?.auditType !== 'removal_only';
    const showRemovalResults = audit?.auditType !== 'posting_only';
    const getCampaignGroupKey = (result: AuditStoreCampaignResult) =>
      `${result.campaignId ?? result.campaignName}-${result.taskName ?? ''}`;
    const uniqueCampaignCount = new Set(selectedCampaignResults.map(getCampaignGroupKey)).size;
    const renderExpectedRows = (results: AuditStoreCampaignResult[]) => {
      if (results.length === 0) {
        return (
          <TableRow>
            <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
              No approved submissions for this cycle.
            </TableCell>
          </TableRow>
        );
      }

      const groups = new Map<string, AuditStoreCampaignResult[]>();
      for (const result of results) {
        const key = getCampaignGroupKey(result);
        groups.set(key, [...(groups.get(key) ?? []), result]);
      }

      return Array.from(groups.entries()).flatMap(([groupKey, groupResults]) => {
        const first = groupResults[0];
        const approvedTotal = groupResults.reduce((sum, result) => sum + result.approvedQuantity, 0);
        const verifiedTotal = groupResults.reduce((sum, result) => sum + (result.submittedQuantity ?? 0), 0);
        const hasVerifiedAll = groupResults.every(r => r.submittedQuantity !== null);

        return [
          <TableRow key={`${groupKey}-header`} className="bg-slate-50 hover:bg-slate-50 border-t">
            <TableCell colSpan={5} className="py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{first.campaignName}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {first.campaignId ? `#${first.campaignId}` : 'Unassigned campaign'}
                    {first.taskName ? ` / ${first.taskName}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{groupResults.length} format{groupResults.length === 1 ? '' : 's'}</Badge>
                  <Badge variant="outline">Approved qty {approvedTotal}</Badge>
                  {hasVerifiedAll && (
                    <Badge variant={approvedTotal === verifiedTotal ? 'secondary' : 'destructive'} className={cn(approvedTotal === verifiedTotal && 'bg-emerald-50 text-emerald-800 border-emerald-100')}>
                      Verified qty {verifiedTotal}
                    </Badge>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-2 h-7 px-2.5 text-xs flex items-center gap-1.5"
                    onClick={() => setSelectedAuditResult(first)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View Details
                  </Button>
                </div>
              </div>
            </TableCell>
          </TableRow>,
          ...groupResults.map((result) => {
            const isDiscrepancy = result.submittedQuantity !== null && result.submittedQuantity !== result.approvedQuantity;

            return (
              <TableRow key={result.key}>
                <TableCell className="pl-8">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{result.formatName}</p>
                    {result.formatId && <p className="text-xs text-gray-500">Format #{result.formatId}</p>}
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium">{result.approvedQuantity}</TableCell>
                <TableCell>
                  {result.submittedQuantity === null ? (
                    <span className="text-xs text-muted-foreground italic">Pending verify</span>
                  ) : (
                    <Badge
                      className={cn(
                        "text-xs font-semibold py-0.5",
                        isDiscrepancy
                          ? "bg-rose-50 text-rose-800 border-rose-100"
                          : "bg-emerald-50 text-emerald-800 border-emerald-100 hover:bg-emerald-50"
                      )}
                      variant="outline"
                    >
                      {result.submittedQuantity} verified
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {result.condition ? (
                    <Badge
                      className={cn(
                        "text-[10px] uppercase font-bold tracking-wide",
                        result.condition === 'excellent' || result.condition === 'good'
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                          : result.condition === 'damaged'
                          ? "bg-red-50 text-red-700 border-red-100 hover:bg-red-50"
                          : "bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-50"
                      )}
                      variant="outline"
                    >
                      {result.condition}
                    </Badge>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs text-gray-600" title={result.comments}>
                  {result.comments || <span className="text-gray-400">—</span>}
                </TableCell>
              </TableRow>
            );
          }),
        ];
      });
    };

    return (
      <main className="min-h-screen bg-gray-50 p-6">
        <div className="space-y-6">
          <PageHeader
            icon={ClipboardCheck}
            title={audit?.name ?? 'View Audit'}
            description={audit
              ? `${audit.retailerName} / ${audit.cycleName} / ${AUDIT_TYPE_LABELS[audit.auditType]}`
              : 'Loading audit details...'}
            className="md:justify-between"
          >
            <Button variant="outline" onClick={() => navigate('/admin/audits')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Audits
            </Button>
          </PageHeader>

          {auditDetailQuery.isLoading && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Loading audit...
              </CardContent>
            </Card>
          )}

          {auditDetailQuery.isError && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="font-medium text-gray-900">Could not load this audit</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  The audit may have been removed or you may not have access.
                </p>
              </CardContent>
            </Card>
          )}

          {audit && (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Start date</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900">{formatDate(audit.auditStartDate)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Stores</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900">{stores.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Cycle</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900">{audit.cycleName}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{formatDateRange(audit.startDate, audit.endDate)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Findings</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900">{totalFindings}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Send className="h-4 w-4" />
                      Auditor dispatches
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Send each auditor their bucket of stores. They only see the audit once it has been sent.
                    </p>
                  </div>
                  {hasUnsentDispatch && (
                    <Button
                      size="sm"
                      disabled={dispatchActionPending}
                      onClick={() => sendDispatchMutation.mutate({ includeBriefing: true })}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Send all
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  {dispatches.length === 0 && (
                    <p className="text-sm text-muted-foreground">No auditor dispatches for this audit yet.</p>
                  )}
                  {dispatches.map((dispatch) => {
                    const statusLabel: Record<AuditDispatch['status'], string> = {
                      draft: 'Draft',
                      sent: 'Sent',
                      in_progress: 'In progress',
                      submitted: 'Submitted',
                      recalled: 'Recalled',
                    };
                    const statusClass: Record<AuditDispatch['status'], string> = {
                      draft: 'bg-gray-100 text-gray-700',
                      sent: 'bg-blue-100 text-blue-700',
                      in_progress: 'bg-amber-100 text-amber-800',
                      submitted: 'bg-green-100 text-green-700',
                      recalled: 'bg-red-100 text-red-700',
                    };
                    const isSent = dispatch.status === 'sent' || dispatch.status === 'in_progress';
                    const isDraftOrRecalled = dispatch.status === 'draft' || dispatch.status === 'recalled';
                    const briefingChecked = briefingOverrides[dispatch.id] ?? dispatch.includeBriefing;
                    return (
                      <div key={dispatch.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{dispatch.auditorName}</p>
                          <p className="text-xs text-muted-foreground">
                            {dispatch.storeCount} store{dispatch.storeCount === 1 ? '' : 's'}
                            {' · '}{dispatch.submittedStoreCount}/{dispatch.storeCount} submitted
                            {dispatch.sentAt ? ` · sent ${formatDate(dispatch.sentAt)}` : ''}
                            {isSent ? (dispatch.includeBriefing ? ' · briefing on' : ' · no briefing') : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass[dispatch.status]}`}>
                            {statusLabel[dispatch.status]}
                          </span>
                          {isDraftOrRecalled && (
                            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <input
                                type="checkbox"
                                className="h-3.5 w-3.5"
                                checked={briefingChecked}
                                onChange={(e) => setBriefingOverrides((prev) => ({ ...prev, [dispatch.id]: e.target.checked }))}
                              />
                              Briefing
                            </label>
                          )}
                          {isDraftOrRecalled && (
                            <Button
                              size="sm"
                              disabled={dispatchActionPending}
                              onClick={() => sendDispatchMutation.mutate({ auditorId: dispatch.auditorId, includeBriefing: briefingChecked })}
                            >
                              {dispatch.status === 'recalled' ? 'Resend' : 'Send'}
                            </Button>
                          )}
                          {isSent && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={dispatchActionPending}
                              onClick={() => recallDispatchMutation.mutate({ auditorId: dispatch.auditorId })}
                            >
                              Recall
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle>Audit Results</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Select a store to review the campaign-level audit checks for that location.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{audit.status.replaceAll('_', ' ')}</Badge>
                    <Badge variant="outline">Cycle {audit.cycleName}</Badge>
                    <Badge variant="outline">{formatDateRange(audit.startDate, audit.endDate)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tab Switcher */}
                  <div className="flex items-center rounded-lg border bg-muted p-1 w-fit">
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('stores')}
                      className={cn(
                        "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                        activeDetailTab === 'stores' ? "bg-white text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      By Store
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('campaigns')}
                      className={cn(
                        "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                        activeDetailTab === 'campaigns' ? "bg-white text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      By Campaign
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveDetailTab('auditors')}
                      className={cn(
                        "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                        activeDetailTab === 'auditors' ? "bg-white text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      By Auditor
                    </button>
                  </div>

                  {activeDetailTab === 'stores' && (
                    <>
                      <div className="overflow-x-auto">
                        <DataTable
                          columns={detailStoresColumns}
                          data={stores}
                          searchKey="storeName"
                          searchPlaceholder="Search stores..."
                          showColumnToggle={false}
                          onRowClick={(row) => setSelectedAuditStoreId(row.id)}
                          getRowClassName={(row) => {
                            return row.id === selectedAuditStoreId ? "bg-blue-50 hover:bg-blue-50" : "";
                          }}
                        />
                      </div>

                      {selectedAuditStore && (
                        <div className="rounded-md border bg-white">
                          <div className="flex flex-col gap-3 border-b px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <Store className="h-5 w-5 text-gray-500" />
                                <h3 className="text-lg font-semibold text-gray-900">{selectedAuditStore.storeName}</h3>
                              </div>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {selectedAuditStore.storeAddress || selectedAuditStore.storeRegion || 'No address'}
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                Auditor: {selectedAuditStore.auditorName}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="secondary">{uniqueCampaignCount} campaigns</Badge>
                              <Badge variant="outline">{selectedCampaignResults.length} audit lines</Badge>
                              <Badge variant="outline">{formatPercent(selectedAuditStore.complianceScore)}</Badge>
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Format</TableHead>
                                  <TableHead className="text-right">Approved Qty (Operator)</TableHead>
                                  <TableHead>Verified Qty (Auditor)</TableHead>
                                  <TableHead>Condition</TableHead>
                                  <TableHead>Auditor Comments</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {showPostingResults && (
                                  <>
                                    <TableRow className="bg-gray-50">
                                      <TableCell colSpan={5} className="py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Postings
                                      </TableCell>
                                    </TableRow>
                                    {renderExpectedRows(postingResults)}
                                  </>
                                )}
                                {showRemovalResults && (
                                  <>
                                    <TableRow className="bg-gray-50">
                                      <TableCell colSpan={5} className="py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Removals
                                      </TableCell>
                                    </TableRow>
                                    {renderExpectedRows(removalResults)}
                                  </>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}

                      {selectedAuditStore && (
                        <>
                          {selectedAuditStore.auditResponse ? (
                            <Card className="mt-6 border-slate-200">
                              <CardHeader className="bg-slate-50 border-b pb-4">
                                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                                  <ClipboardCheck className="h-5 w-5 text-indigo-600" />
                                  Auditor Survey & Checklist Responses
                                </CardTitle>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Survey responses and opening checklist recorded by {selectedAuditStore.auditorName} on {selectedAuditStore.submittedAt ? formatDate(selectedAuditStore.submittedAt) : 'N/A'}.
                                </p>
                              </CardHeader>
                              <CardContent className="pt-6 space-y-6">
                                {/* 1. Briefing & Opening Checklist Stage */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider border-b pb-1">Stage 1: Briefing & Opening Checklist</h4>
                                  <div className="bg-slate-50 border rounded-md p-3 text-xs space-y-2">
                                    <p className="flex justify-between items-center">
                                      <span className="font-semibold text-slate-600">Briefing Acknowledged:</span>
                                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
                                        {selectedAuditStore.auditResponse.openingAcknowledgedAt 
                                          ? `Yes, at ${new Date(selectedAuditStore.auditResponse.openingAcknowledgedAt).toLocaleTimeString()}`
                                          : 'No'}
                                      </Badge>
                                    </p>
                                    {selectedAuditStore.auditResponse.answers?.filter((ans: any) => ans.sectionId === 'briefing').length > 0 && (
                                      <div className="space-y-1 pt-1 border-t">
                                        <p className="font-semibold text-slate-600 mb-1.5">Checklist Acknowledged:</p>
                                        {selectedAuditStore.auditResponse.answers
                                          ?.filter((ans: any) => ans.sectionId === 'briefing')
                                          .map((ans: any) => {
                                            const isChecked = ans.answer === 'yes' || ans.answer === true || ans.answer === 'true';
                                            return (
                                              <div key={ans.questionId} className="flex items-center justify-between py-0.5">
                                                <span className="text-slate-700">{ans.questionLabel}</span>
                                                <Badge variant={isChecked ? 'secondary' : 'outline'} className={isChecked ? 'bg-emerald-50 text-emerald-800 border-emerald-100' : 'text-gray-400 border-gray-200'}>
                                                  {isChecked ? '✓ Acknowledged' : '✗ Pending'}
                                                </Badge>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* 2. Custom Questionnaire / Survey Answers */}
                                {selectedAuditStore.auditResponse.answers?.filter((ans: any) => ans.sectionId !== 'briefing' && ans.sectionId !== 'core-rules').length > 0 && (
                                  <div className="space-y-3">
                                    <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider border-b pb-1">Stage 2: Custom Questionnaire & Survey Answers</h4>
                                    <div className="space-y-3">
                                      {(() => {
                                        const surveyAns = selectedAuditStore.auditResponse.answers?.filter((ans: any) => ans.sectionId !== 'briefing' && ans.sectionId !== 'core-rules') || [];
                                        const sectionsMap = new Map<string, any[]>();
                                        for (const ans of surveyAns) {
                                          const title = ans.sectionTitle || ans.sectionId || 'General Settings';
                                          if (!sectionsMap.has(title)) sectionsMap.set(title, []);
                                          sectionsMap.get(title)!.push(ans);
                                        }
                                        return Array.from(sectionsMap.entries()).map(([title, answers]) => (
                                          <div key={title} className="space-y-2 border rounded-md p-3 bg-white">
                                            <h5 className="font-semibold text-xs text-slate-800">{title}</h5>
                                            <div className="divide-y text-xs">
                                              {answers.map((ans: any) => {
                                                const isFinding = ans.answer === 'no' || ans.answer === 'false' || ans.answer === false;
                                                const displayAnswer = typeof ans.answer === 'boolean' 
                                                  ? (ans.answer ? 'Yes' : 'No') 
                                                  : (ans.answer || '—');

                                                return (
                                                  <div key={ans.questionId} className="py-2.5 first:pt-0 last:pb-0 space-y-1">
                                                    <div className="flex items-start justify-between gap-4">
                                                      <span className="text-slate-800 font-medium">{ans.questionLabel}</span>
                                                      <Badge 
                                                        variant={isFinding ? 'destructive' : 'outline'} 
                                                        className={cn(
                                                          "text-[10px] py-0.5",
                                                          !isFinding && (ans.answer === 'yes' || ans.answer === 'true' || ans.answer === true)
                                                            ? "bg-emerald-50 text-emerald-800 border-emerald-100 hover:bg-emerald-50"
                                                            : ""
                                                        )}
                                                      >
                                                        {displayAnswer}
                                                      </Badge>
                                                    </div>
                                                    {ans.notes && (
                                                      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100/50 rounded-sm px-2 py-1 italic mt-1">
                                                        Notes: "{ans.notes}"
                                                      </p>
                                                    )}
                                                    {ans.evidenceAssetIds && ans.evidenceAssetIds.length > 0 && (
                                                      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100/50 rounded-sm px-2 py-1">
                                                        <Camera className="h-3 w-3" />
                                                        <span>{ans.evidenceAssetIds.length} photo(s) attached (Asset IDs: {ans.evidenceAssetIds.join(', ')})</span>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ));
                                      })()}
                                    </div>
                                  </div>
                                )}

                                {/* 3. Stage 3: Closure & Remarks */}
                                <div className="space-y-3">
                                  <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider border-b pb-1">Stage 3: Closure & Remarks</h4>
                                  <div className="bg-slate-50 border rounded-md p-3 text-xs space-y-2">
                                    <div>
                                      <p className="font-semibold text-slate-600 mb-1">Auditor Closing Remarks:</p>
                                      <p className="text-slate-700 bg-white border rounded p-2.5 italic">
                                        {selectedAuditStore.auditResponse.closingRemarks?.trim() || 'No closing remarks recorded.'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card className="mt-6 border-dashed border-slate-200">
                              <CardContent className="py-8 text-center text-sm text-muted-foreground italic bg-slate-50/50">
                                No structured auditor responses submitted for this store.
                              </CardContent>
                            </Card>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {activeDetailTab === 'campaigns' && (() => {
                    const selectedCampaign = campaignsList.find(c => c.campaignName === selectedCampaignKey) ?? campaignsList[0] ?? null;

                    return (
                      <div className="space-y-6">
                        <div className="overflow-x-auto bg-white border rounded-lg">
                          <DataTable
                            columns={detailCampaignsColumns}
                            data={campaignsList}
                            searchKey="campaignName"
                            searchPlaceholder="Search campaigns..."
                            showColumnToggle={false}
                            onRowClick={(row) => setSelectedCampaignKey(row.campaignName)}
                            getRowClassName={(row) => {
                              return row.campaignName === selectedCampaignKey ? "bg-blue-50 hover:bg-blue-50" : "";
                            }}
                          />
                        </div>

                        {selectedCampaign && (
                          <div className="rounded-md border bg-white">
                            <div className="flex flex-col gap-3 border-b px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <LayoutDashboard className="h-5 w-5 text-gray-500" />
                                  <h3 className="text-lg font-semibold text-gray-900">{selectedCampaign.campaignName}</h3>
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  Breakdown of auditor findings across all stores for this campaign.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="secondary">{selectedCampaign.storeIds.size} stores</Badge>
                                <Badge variant="outline">{selectedCampaign.lines.length} verified lines</Badge>
                                <Badge variant="outline">Approved qty {selectedCampaign.approvedQuantity}</Badge>
                                <Badge variant="outline">Verified qty {selectedCampaign.submittedQuantity}</Badge>
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Store & Auditor</TableHead>
                                    <TableHead>Format</TableHead>
                                    <TableHead className="text-right">Approved Qty</TableHead>
                                    <TableHead>Verified Qty</TableHead>
                                    <TableHead>Condition</TableHead>
                                    <TableHead>Auditor Comments</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {selectedCampaign.lines.map((line, index) => {
                                    const isDiscrepancy = line.submittedQuantity !== null && line.submittedQuantity !== line.approvedQuantity;
                                    return (
                                      <TableRow key={index}>
                                        <TableCell>
                                          <div>
                                            <p className="font-semibold text-gray-900">{line.storeName}</p>
                                            <p className="text-xs text-gray-500">Auditor: {line.auditorName}</p>
                                          </div>
                                        </TableCell>
                                        <TableCell>{line.formatName}</TableCell>
                                        <TableCell className="text-right">{line.approvedQuantity}</TableCell>
                                        <TableCell>
                                          {line.submittedQuantity === null ? (
                                            <span className="text-xs text-muted-foreground italic">Pending verify</span>
                                          ) : (
                                            <Badge
                                              className={cn(
                                                "text-xs font-semibold py-0.5",
                                                isDiscrepancy
                                                  ? "bg-rose-50 text-rose-800 border-rose-100"
                                                  : "bg-emerald-50 text-emerald-800 border-emerald-100"
                                              )}
                                              variant="outline"
                                            >
                                              {line.submittedQuantity} verified
                                            </Badge>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {line.condition ? (
                                            <Badge
                                              className={cn(
                                                "text-[10px] uppercase font-bold tracking-wide",
                                                line.condition === 'excellent' || line.condition === 'good'
                                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                  : line.condition === 'damaged'
                                                  ? "bg-red-50 text-red-700 border-red-100"
                                                  : "bg-amber-50 text-amber-700 border-amber-100"
                                              )}
                                              variant="outline"
                                            >
                                              {line.condition}
                                            </Badge>
                                          ) : (
                                            <span className="text-gray-400">—</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="max-w-[200px] truncate text-xs text-gray-600" title={line.comments}>
                                          {line.comments || <span className="text-gray-400">—</span>}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSelectedAuditResult(line.result)}
                                          >
                                            <Eye className="mr-2 h-4 w-4" />
                                            View Details
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {activeDetailTab === 'auditors' && (() => {
                    const selectedAuditor = auditorsList.find(a => a.auditorId === selectedAuditorId) ?? auditorsList[0] ?? null;

                    return (
                      <div className="space-y-6">
                        <div className="overflow-x-auto bg-white border rounded-lg">
                          <DataTable
                            columns={detailAuditorsColumns}
                            data={auditorsList}
                            searchKey="auditorName"
                            searchPlaceholder="Search auditors..."
                            showColumnToggle={false}
                            onRowClick={(row) => setSelectedAuditorId(row.auditorId)}
                            getRowClassName={(row) => {
                              return row.auditorId === selectedAuditorId ? "bg-blue-50 hover:bg-blue-50" : "";
                            }}
                          />
                        </div>

                        {selectedAuditor && (
                          <div className="rounded-md border bg-white">
                            <div className="flex flex-col gap-3 border-b px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <User className="h-5 w-5 text-gray-500" />
                                  <h3 className="text-lg font-semibold text-gray-900">{selectedAuditor.auditorName}</h3>
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  Stores assigned to this auditor.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="secondary">{selectedAuditor.expectedStores} stores</Badge>
                                <Badge variant="outline">{selectedAuditor.completedStores} / {selectedAuditor.expectedStores} completed</Badge>
                                <Badge variant="outline">Total approved qty {selectedAuditor.expectedQuantity}</Badge>
                                <Badge variant="outline">Findings {selectedAuditor.findingCount}</Badge>
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Store</TableHead>
                                    <TableHead className="text-right">Campaigns</TableHead>
                                    <TableHead className="text-right">Approved Qty</TableHead>
                                    <TableHead>Audit Status</TableHead>
                                    <TableHead className="text-right">Findings</TableHead>
                                    <TableHead className="text-right">Score</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {selectedAuditor.storesList.map((store) => (
                                    <TableRow key={store.id}>
                                      <TableCell>
                                        <div>
                                          <p className="font-semibold text-gray-900">{store.storeName}</p>
                                          <p className="text-xs text-gray-500">{store.storeAddress || store.storeRegion || 'No address'}</p>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">{store.expectedCampaignCount}</TableCell>
                                      <TableCell className="text-right">{store.expectedQuantity}</TableCell>
                                      <TableCell>
                                        <Badge
                                          variant={store.resultStatus === 'not_started' ? 'outline' : 'secondary'}
                                          className={cn(store.resultStatus === 'not_started' && 'text-gray-600')}
                                        >
                                          {formatAuditStoreStatus(store.resultStatus)}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-right">{store.findingCount}</TableCell>
                                      <TableCell className="text-right font-semibold">
                                        {store.complianceScore !== null ? formatPercent(store.complianceScore) : <span className="text-gray-400">—</span>}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedAuditStoreId(store.id);
                                            setActiveDetailTab('stores');
                                          }}
                                        >
                                          <Eye className="mr-2 h-4 w-4" />
                                          Inspect Store
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              <Dialog open={!!selectedAuditResult} onOpenChange={(open) => !open && setSelectedAuditResult(null)}>
                <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Review Submission</DialogTitle>
                  </DialogHeader>
                  {selectedAuditResult && (
                    <div className="space-y-5">
                      <Card>
                        <CardContent className="pt-5">
                          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                            <div>
                              <h2 className="text-xl font-semibold leading-tight">{dialogStore?.storeName ?? 'Store'}</h2>
                              <p className="text-xs text-muted-foreground">
                                Submission {selectedAuditResult.postingEventId ? `#${selectedAuditResult.postingEventId}` : 'not submitted'}
                              </p>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Badge variant="outline">{selectedAuditResult.campaignName}</Badge>
                                <Badge variant="secondary">{selectedAuditResult.auditType}</Badge>
                                <Badge variant="outline">Cycle {audit.cycleName}</Badge>
                              </div>
                            </div>

                            <div className="grid gap-2 text-sm text-muted-foreground xl:text-right">
                              <div className="flex items-center gap-2 xl:justify-end">
                                <User className="h-4 w-4" />
                                <span>{dialogStore?.auditorName ?? 'Assigned auditor'}</span>
                              </div>
                              {selectedAuditResult.submittedAt && (
                                <div className="flex items-center gap-2 xl:justify-end">
                                  <CalendarClock className="h-4 w-4" />
                                  <span>{formatDate(selectedAuditResult.submittedAt)}</span>
                                </div>
                              )}
                              {dialogStore?.storeAddress && (
                                <div className="flex items-center gap-2 xl:justify-end">
                                  <MapPin className="h-4 w-4" />
                                  <span>{dialogStore.storeAddress}</span>
                                </div>
                              )}
                              <div className="text-xs">
                                {selectedAuditResult.taskName || 'Campaign proof'}
                                {selectedAuditResult.campaignId ? ` / #${selectedAuditResult.campaignId}` : ''}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {selectedAuditResult.reviewDecision && (
                        <Card className={selectedAuditResult.reviewDecision === 'APPROVE' ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}>
                          <CardContent className="pt-4 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className={selectedAuditResult.reviewDecision === 'APPROVE' ? 'h-4 w-4 text-green-700' : 'h-4 w-4 text-red-700'} />
                                <span className="text-sm font-medium">
                                  Reviewed: {selectedAuditResult.reviewDecision === 'APPROVE' ? 'Approved' : 'Rejected'}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {selectedAuditResult.reviewedByName || 'Reviewer'}
                                {selectedAuditResult.reviewedAt ? ` / ${formatDate(selectedAuditResult.reviewedAt)}` : ''}
                              </span>
                            </div>
                            <div className="rounded bg-white/70 px-3 py-2 text-sm">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Admin notes</p>
                              <p className="mt-1 whitespace-pre-wrap">
                                {selectedAuditResult.reviewAdminNotes?.trim()
                                  || selectedAuditResult.reviewFeedbackText?.trim()
                                  || selectedAuditResult.reviewRejectionNotes?.trim()
                                  || 'No admin notes yet'}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <div className="grid gap-4 xl:grid-cols-2">
                        {/* Operator Proof Details */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base flex items-center justify-between">
                              <span>Operator Proof (Reference)</span>
                              <Badge variant="outline">{selectedAuditResult.imageCount} photo{selectedAuditResult.imageCount === 1 ? '' : 's'}</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4 text-sm">
                            <div className="rounded-md border p-3 bg-gray-50 space-y-2">
                              <p className="text-xs text-muted-foreground font-semibold uppercase">Approved Formats & Quantities</p>
                              <div className="space-y-1">
                                {selectedGroupResults.map((r, idx) => (
                                  <div key={idx} className="flex justify-between text-sm">
                                    <span className="text-gray-700">{r.formatName}</span>
                                    <span className="font-bold">{r.approvedQuantity}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Operator Notes</p>
                              <p className="mt-1 whitespace-pre-wrap rounded-md border bg-gray-50 p-3 text-xs">
                                {selectedAuditResult.submittedNotes?.trim() || 'No operator notes submitted'}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Operator Photos</p>
                              {selectedAuditResult.imageUrls.length > 0 ? (
                                <div className="space-y-2">
                                  <a href={selectedAuditResult.imageUrls[0]} target="_blank" rel="noopener noreferrer" className="block border rounded-lg overflow-hidden bg-gray-100">
                                    <img
                                      src={selectedAuditResult.imageUrls[0]}
                                      alt="Operator proof evidence"
                                      className="max-h-[300px] w-full object-contain mx-auto"
                                    />
                                  </a>
                                  {selectedAuditResult.imageUrls.length > 1 && (
                                    <div className="grid grid-cols-3 gap-2">
                                      {selectedAuditResult.imageUrls.slice(1, 4).map((url, idx) => (
                                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block border rounded overflow-hidden">
                                          <img src={url} alt="Additional proof" className="h-16 w-full object-cover" />
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 italic rounded-md border border-dashed p-4 text-center">No images uploaded by operator</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Auditor Findings */}
                        <Card className="border-blue-100">
                          <CardHeader className="bg-blue-50/50">
                            <CardTitle className="text-base text-blue-900 flex items-center justify-between">
                              <span>Auditor Verification (Actuals)</span>
                              <Badge className="bg-blue-600 text-white font-semibold">Auditor Log</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-6 text-sm pt-4">
                            {selectedGroupResults.map((result, idx) => {
                              const isDiscrepancy = result.submittedQuantity !== null && result.submittedQuantity !== result.approvedQuantity;
                              return (
                                <div key={idx} className="border-b pb-4 last:border-b-0 last:pb-0 space-y-3">
                                  <div className="flex items-center justify-between">
                                    <p className="font-semibold text-gray-950">{result.formatName}</p>
                                    <p className="text-xs text-muted-foreground font-medium">Approved: {result.approvedQuantity}</p>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div className={cn(
                                      "rounded-md border p-3",
                                      result.submittedQuantity === null
                                        ? "bg-slate-50 border-slate-200"
                                        : result.submittedQuantity === result.approvedQuantity
                                        ? "bg-emerald-50 border-emerald-100"
                                        : "bg-rose-50 border-rose-100"
                                    )}>
                                      <p className="text-xs text-muted-foreground font-semibold uppercase">Verified Quantity</p>
                                      <p className="mt-1 text-lg font-bold">
                                        {result.submittedQuantity !== null ? result.submittedQuantity : 'Pending'}
                                      </p>
                                    </div>
                                    <div className="rounded-md border p-3 bg-slate-50">
                                      <p className="text-xs text-muted-foreground font-semibold uppercase">Condition Status</p>
                                      {result.condition ? (
                                        <Badge
                                          className={cn(
                                            "mt-1 uppercase text-[10px] font-bold tracking-wide",
                                            result.condition === 'excellent' || result.condition === 'good'
                                              ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-50"
                                              : result.condition === 'damaged'
                                              ? "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-50"
                                              : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50"
                                          )}
                                          variant="outline"
                                        >
                                          {result.condition}
                                        </Badge>
                                      ) : (
                                        <p className="mt-1 text-sm font-semibold text-gray-500">Not rated</p>
                                      )}
                                    </div>
                                  </div>

                                  {result.comments?.trim() && (
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auditor Comments</p>
                                      <p className="mt-1 whitespace-pre-wrap rounded-md border bg-slate-50 p-2.5 text-xs italic">
                                        {result.comments}
                                      </p>
                                    </div>
                                  )}

                                  {result.evidenceAssetIds && result.evidenceAssetIds.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Auditor Photos</p>
                                      <div className="rounded-md border border-dashed border-blue-200 bg-blue-50/20 p-3 text-center">
                                        <Camera className="h-5 w-5 text-blue-500 mx-auto" />
                                        <p className="mt-1 text-xs font-semibold text-blue-900">
                                          {result.evidenceAssetIds.length} verification photo(s) attached
                                        </p>
                                        <p className="text-[10px] text-muted-foreground mt-0.5">Asset IDs: {result.evidenceAssetIds.join(', ')}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>


            </>
          )}
        </div>
      </main>
    );
  }

  if (showTemplates) {
    return (
      <main className="min-h-screen bg-gray-50 p-6">
        <div className="space-y-6">
          <PageHeader
            icon={FileText}
            title="Audit Templates"
            description="Manage the questions auditors answer for each audit type."
            className="md:justify-between"
          >
            <Button
              onClick={() => {
                const draft = createEmptyTemplateDraft();
                setSelectedTemplateId(null);
                setTemplateDraft(draft);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/audits')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Audits
            </Button>
          </PageHeader>

          <div className="grid gap-6 2xl:grid-cols-[340px_1fr_330px]">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle>Templates</CardTitle>
                  {auditTemplates.some((t) => t.archivedAt) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => setShowArchivedTemplates((v) => !v)}
                    >
                      {showArchivedTemplates ? 'Hide archived' : 'Show archived'}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {auditTemplates.filter((t) => !t.archivedAt).length} active
                  {auditTemplates.some((t) => t.archivedAt)
                    ? ` · ${auditTemplates.filter((t) => t.archivedAt).length} archived`
                    : ''}
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {templatesQuery.isLoading && (
                  <p className="text-sm text-gray-500">Loading templates...</p>
                )}
                {!templatesQuery.isLoading && auditTemplates.length === 0 && (
                  <p className="text-sm text-gray-500">No templates yet.</p>
                )}
                {auditTemplates
                  .filter((template) => showArchivedTemplates || !template.archivedAt)
                  .map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => selectTemplate(template)}
                    className={`w-full rounded-md border p-3 text-left transition-colors ${
                      selectedTemplateId === template.id ? 'border-primary bg-blue-50' : 'bg-white hover:bg-gray-50'
                    } ${template.archivedAt ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{template.name}</p>
                        <p className="mt-1 text-xs text-gray-500">{AUDIT_TYPE_LABELS[template.auditType]}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {template.archivedAt && <Badge variant="outline">Archived</Badge>}
                        {template.isDefault && <Badge variant="secondary">Default</Badge>}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {template.sections.length} sections, {countTemplateQuestions(template)} questions
                    </p>
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card>
              {templateDraft ? (
                <>
                  <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <CardTitle>{templateDraft.id ? 'Edit Template' : 'Create Template'}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {templateDraft.sections.length} sections, {countTemplateQuestions(templateDraft)} questions
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {templateDraft.id && (
                        <Button
                          type="button"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          disabled={deleteTemplateMutation.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete ${templateDraft.name}?`)) {
                              deleteTemplateMutation.mutate(templateDraft.id as number);
                            }
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      )}
                      {templateDraft.id && (() => {
                        const archived = Boolean(auditTemplates.find((t) => t.id === templateDraft.id)?.archivedAt);
                        return (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={archiveTemplateMutation.isPending}
                            onClick={() =>
                              archiveTemplateMutation.mutate({
                                templateId: templateDraft.id as number,
                                archive: !archived,
                              })
                            }
                          >
                            {archived ? (
                              <><ArchiveRestore className="mr-2 h-4 w-4" />Restore</>
                            ) : (
                              <><Archive className="mr-2 h-4 w-4" />Archive</>
                            )}
                          </Button>
                        );
                      })()}
                      <Button
                        type="button"
                        disabled={!templateDraft.name.trim() || saveTemplateMutation.isPending}
                        onClick={() => saveTemplateMutation.mutate(templateDraft)}
                      >
                        {saveTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
                      <div className="space-y-2">
                        <Label>Template name</Label>
                        <Input
                          value={templateDraft.name}
                          onChange={(event) => setTemplateDraft((draft) => draft ? { ...draft, name: event.target.value } : draft)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Audit type</Label>
                        <Select
                          value={templateDraft.auditType}
                          onValueChange={(value) => setTemplateDraft((draft) => draft ? { ...draft, auditType: value as AuditType } : draft)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(AUDIT_TYPE_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Tabs for Stage-based Template Configuration */}
                    <div className="flex border-b border-gray-200 shrink-0">
                      <button
                        type="button"
                        className={cn(
                          "px-4 py-2 text-sm font-semibold border-b-2 transition-colors",
                          templateTab === 'briefing' ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
                        )}
                        onClick={() => setTemplateTab('briefing')}
                      >
                        1. Opening Briefing
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "px-4 py-2 text-sm font-semibold border-b-2 transition-colors",
                          templateTab === 'rules' ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
                        )}
                        onClick={() => setTemplateTab('rules')}
                      >
                        2. Core Rules
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "px-4 py-2 text-sm font-semibold border-b-2 transition-colors",
                          templateTab === 'closure' ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
                        )}
                        onClick={() => setTemplateTab('closure')}
                      >
                        3. Closing Feedback
                      </button>
                    </div>

                    {/* Tab Panels */}
                    {templateTab === 'briefing' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Briefing Instructions Text</Label>
                          <Textarea
                            value={templateDraft.openingText}
                            onChange={(event) => setTemplateDraft((draft) => draft ? { ...draft, openingText: event.target.value } : draft)}
                            rows={4}
                            placeholder="Add customizable safety protocols, standard instructions, and briefings that the auditor must read and acknowledge before commencing work."
                          />
                        </div>

                        {/* Map Briefing Sections */}
                        {(() => {
                          const bSections = templateDraft.sections.filter(s => s.phase === 'briefing' || s.id === 'briefing');
                          if (bSections.length === 0) {
                            return (
                              <div className="rounded-md border bg-slate-50 border-dashed p-6 text-center space-y-3">
                                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">No Checklist Sections</p>
                                  <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                                    No custom checklists initialized. The mobile app will display the default verification safety list (PPE, Sign-in desk, Hazard check).
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setTemplateDraft(draft => {
                                      if (!draft) return draft;
                                      return {
                                        ...draft,
                                        sections: [
                                          ...draft.sections,
                                          {
                                            id: 'briefing',
                                            title: 'Safety & Prep Checklist',
                                            phase: 'briefing',
                                            questions: [
                                              { id: makeDraftId('question'), label: 'Wore required PPE (high-vis, boots)', type: 'yes_no', scope: 'per_store', required: true },
                                              { id: makeDraftId('question'), label: 'Signed in at Store Management desk', type: 'yes_no', scope: 'per_store', required: true },
                                              { id: makeDraftId('question'), label: 'Performed dynamic hazard assessment', type: 'yes_no', scope: 'per_store', required: true }
                                            ]
                                          }
                                        ]
                                      };
                                    });
                                  }}
                                >
                                  <Plus className="mr-1 h-3.5 w-3.5" /> Initialize Default Checklist
                                </Button>
                              </div>
                            );
                          }

                          return (
                            <div className="space-y-4">
                              {bSections.map((section) => (
                                <div key={section.id} className="rounded-md border bg-white p-4 space-y-4 animate-in fade-in duration-100">
                                  <div className="flex items-center justify-between border-b pb-2 gap-3">
                                    <div className="flex-1">
                                      <Input
                                        value={section.title}
                                        onChange={(event) => updateTemplateSection(section.id, { title: event.target.value })}
                                        placeholder="Checklist Section Title"
                                        className="font-semibold text-gray-900 border-none shadow-none focus-visible:ring-1 px-1 h-8 text-sm focus:bg-slate-50"
                                      />
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 h-8 px-2"
                                      onClick={() => removeTemplateSection(section.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  <div className="space-y-3">
                                    {section.questions.map((q, qIndex) => (
                                      <div key={q.id} className="flex gap-2 items-center">
                                        <Input
                                          value={q.label}
                                          onChange={(event) => updateTemplateQuestion(section.id, q.id, { label: event.target.value })}
                                          placeholder={`Checklist item #${qIndex + 1}`}
                                          className="flex-1 text-sm h-9"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600 hover:text-red-700 h-9 px-2"
                                          onClick={() => removeTemplateQuestion(section.id, q.id)}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}

                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setTemplateDraft(draft => {
                                          if (!draft) return draft;
                                          return {
                                            ...draft,
                                            sections: draft.sections.map(s => {
                                              if (s.id !== section.id) return s;
                                              return {
                                                ...s,
                                                questions: [
                                                  ...s.questions,
                                                  { id: makeDraftId('question'), label: '', type: 'yes_no', scope: 'per_store', required: true }
                                                ]
                                              };
                                            })
                                          };
                                        });
                                      }}
                                    >
                                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Checklist Item
                                    </Button>
                                  </div>
                                </div>
                              ))}

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full py-5 border-dashed"
                                onClick={() => {
                                  setTemplateDraft(draft => {
                                    if (!draft) return draft;
                                    return {
                                      ...draft,
                                      sections: [
                                        ...draft.sections,
                                        {
                                          id: makeDraftId('section'),
                                          title: 'Additional Briefing Checklist',
                                          phase: 'briefing',
                                          questions: []
                                        }
                                      ]
                                    };
                                  });
                                }}
                              >
                                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Briefing Checklist Section
                              </Button>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {templateTab === 'rules' && (
                      <div className="space-y-4">
                        <div className="rounded-md border bg-white p-4 space-y-4">
                          <p className="font-semibold text-gray-900 border-b pb-2">Counts Verification Rules</p>
                          <p className="text-xs text-muted-foreground">
                            Verify Counts stage is automatically generated from expected cycle counts. Use these rules to configure verification behavior.
                          </p>

                          {(() => {
                            const cRules = templateDraft.sections.find(s => s.id === 'core-rules');
                            const hasSection = !!cRules;

                            const ensureCoreRulesSection = (draft: AuditTemplateDraft): AuditTemplateDraft => {
                              const existing = draft.sections.find(s => s.id === 'core-rules');
                              if (existing) return draft;
                              return {
                                ...draft,
                                sections: [
                                  ...draft.sections,
                                  {
                                    id: 'core-rules',
                                    title: 'Core Rules',
                                    questions: [
                                      { id: 'require-discrepancy-photos', label: 'Require photo evidence on count mismatch', type: 'yes_no', scope: 'per_store', required: true },
                                      { id: 'enable-condition-logging', label: 'Enable condition logging per format', type: 'yes_no', scope: 'per_store', required: true }
                                    ]
                                  }
                                ]
                              };
                            };

                            const isPhotoRequired = !hasSection || (cRules.questions.find(q => q.id === 'require-discrepancy-photos')?.required !== false);
                            const isConditionEnabled = !hasSection || (cRules.questions.find(q => q.id === 'enable-condition-logging')?.required !== false);

                            const toggleRule = (ruleId: string, currentVal: boolean) => {
                              setTemplateDraft(draft => {
                                if (!draft) return draft;
                                const withSection = ensureCoreRulesSection(draft);
                                return {
                                  ...withSection,
                                  sections: withSection.sections.map(s => {
                                    if (s.id !== 'core-rules') return s;
                                    return {
                                      ...s,
                                      questions: s.questions.map(q => {
                                        if (q.id !== ruleId) return q;
                                        return { ...q, required: !currentVal };
                                      })
                                    };
                                  })
                                };
                              });
                            };

                            return (
                              <div className="space-y-3">
                                <label className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50 hover:bg-slate-100 cursor-pointer transition-colors">
                                  <Checkbox
                                    className="mt-0.5"
                                    checked={isPhotoRequired}
                                    onCheckedChange={() => toggleRule('require-discrepancy-photos', isPhotoRequired)}
                                  />
                                  <div>
                                    <span className="block text-sm font-semibold text-gray-900">Require Photo Evidence on Discrepancy</span>
                                    <span className="block text-xs text-muted-foreground">The auditor must take/attach a photo if the verified count does not match cycle expectations.</span>
                                  </div>
                                </label>

                                <label className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50 hover:bg-slate-100 cursor-pointer transition-colors">
                                  <Checkbox
                                    className="mt-0.5"
                                    checked={isConditionEnabled}
                                    onCheckedChange={() => toggleRule('enable-condition-logging', isConditionEnabled)}
                                  />
                                  <div>
                                    <span className="block text-sm font-semibold text-gray-900">Enable Format Condition Ratings</span>
                                    <span className="block text-xs text-muted-foreground">Let auditors log material condition (Excellent, Good, Fair, Poor, Damaged) per campaign format.</span>
                                  </div>
                                </label>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {templateTab === 'closure' && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Closing instructions prompt</Label>
                          <Textarea
                            value={templateDraft.closingText}
                            onChange={(event) => setTemplateDraft((draft) => draft ? { ...draft, closingText: event.target.value } : draft)}
                            rows={3}
                            placeholder="Shown before the auditor submits this audit."
                          />
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center justify-between border-b pb-2">
                            <div>
                              <p className="font-semibold text-gray-900">Closing Questionnaire Checklist</p>
                              <p className="text-xs text-muted-foreground">Add custom questions for general feedback, site issues, and store manager signatures.</p>
                            </div>
                            <Button type="button" variant="outline" size="sm" onClick={addTemplateSection}>
                              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Section
                            </Button>
                          </div>

                          {closingSections.map((section, sectionIndex) => (
                            <div key={section.id} className="rounded-md border bg-white p-4">
                              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between border-b pb-3 mb-3">
                                <div className="flex-1 space-y-2">
                                  <Label>Section Title</Label>
                                  <Input
                                    value={section.title}
                                    onChange={(event) => updateTemplateSection(section.id, { title: event.target.value })}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 h-9"
                                  onClick={() => removeTemplateSection(section.id)}
                                >
                                  <Trash2 className="mr-1.5 h-4 w-4" /> Remove Section
                                </Button>
                              </div>

                              <div className="space-y-3">
                                {section.questions.map((question) => (
                                  <div key={question.id} className="grid gap-3 rounded-md border bg-gray-50 p-3 xl:grid-cols-[1fr_150px_120px_auto] xl:items-center">
                                    <Input
                                      value={question.label}
                                      onChange={(event) => updateTemplateQuestion(section.id, question.id, { label: event.target.value })}
                                      placeholder="Question prompt (e.g. Manager signed off?)"
                                    />
                                    <select
                                      value={question.type}
                                      onChange={(event) => updateTemplateQuestion(section.id, question.id, { type: event.target.value as AuditQuestionType })}
                                      className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    >
                                      {QUESTION_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                          {QUESTION_TYPE_LABELS[type]}
                                        </option>
                                      ))}
                                    </select>
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                      <Checkbox
                                        checked={question.required}
                                        onCheckedChange={(checked) => updateTemplateQuestion(section.id, question.id, { required: checked === true })}
                                      />
                                      Required
                                    </label>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 justify-self-end"
                                      onClick={() => removeTemplateQuestion(section.id, question.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}

                                <Button type="button" variant="outline" size="sm" onClick={() => addTemplateQuestion(section.id)}>
                                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Question
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <CardHeader>
                  <CardTitle>Select a Template</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Choose a template to edit or create a new one.
                  </p>
                </CardHeader>
              )}
            </Card>

            <div className="hidden 2xl:block sticky top-4 self-start">
              <AuditTemplateMobilePreview template={templateDraft} />
            </div>
          </div>

          <div className="2xl:hidden">
            <Card>
              <CardHeader>
                <CardTitle>Mobile Preview</CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <AuditTemplateMobilePreview template={templateDraft} />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    );
  }

  if (!showCreateWizard) {
    const auditCampaigns = overview?.auditCampaigns ?? [];
    const globalStores = overview?.stores ?? [];
    const globalCampaignLines = overview?.campaignLines ?? [];

    // Group global campaign results across all stores
    const globalCampaignMap = new Map<string, {
      campaignId: number | null;
      campaignName: string;
      expectedQuantity: number;
      approvedQuantity: number;
      submittedQuantity: number;
      findingCount: number;
      verifiedCount: number;
      storeIds: Set<number>;
      lines: Array<{
        storeName: string;
        auditName: string;
        auditorName: string;
        formatName: string;
        approvedQuantity: number;
        submittedQuantity: number | null;
        condition: string | null;
        comments: string;
        result: any;
      }>;
    }>();

    for (const res of globalCampaignLines) {
      const key = `${res.campaignId ?? res.campaignName}`;
      const existing = globalCampaignMap.get(key) ?? {
        campaignId: res.campaignId,
        campaignName: res.campaignName,
        expectedQuantity: 0,
        approvedQuantity: 0,
        submittedQuantity: 0,
        findingCount: 0,
        verifiedCount: 0,
        storeIds: new Set<number>(),
        lines: [] as Array<{
          storeName: string;
          auditName: string;
          auditorName: string;
          formatName: string;
          approvedQuantity: number;
          submittedQuantity: number | null;
          condition: string | null;
          comments: string;
          result: any;
        }>,
      };

      existing.expectedQuantity += res.expectedQuantity;
      if (res.approvedQuantity !== null) {
        existing.approvedQuantity += res.approvedQuantity;
      }
      if (res.verifiedQuantity !== null) {
        existing.submittedQuantity += res.verifiedQuantity;
        existing.verifiedCount++;
        if (res.verifiedQuantity !== res.approvedQuantity || res.verifiedCondition === 'damaged') {
          existing.findingCount++;
        }
      }
      existing.storeIds.add(res.storeId);
      existing.lines.push({
        storeName: res.storeName || `Store #${res.storeId}`,
        auditName: res.auditName,
        auditorName: res.auditorName,
        formatName: res.formatName,
        approvedQuantity: res.approvedQuantity ?? 0,
        submittedQuantity: res.verifiedQuantity,
        condition: res.verifiedCondition,
        comments: res.verifiedComments || '',
        result: res,
      });

      globalCampaignMap.set(key, existing);
    }

    const aggregatedCampaigns = Array.from(globalCampaignMap.values()).sort((a, b) =>
      a.campaignName.localeCompare(b.campaignName)
    );

    // Group global by auditor
    const globalAuditorMap = new Map<number, {
      auditorId: number;
      auditorName: string;
      completedStores: number;
      inProgressStores: number;
      pendingStores: number;
      totalStores: number;
      expectedQuantity: number;
      verifiedQuantity: number;
      findingCount: number;
      verifiedCount: number;
      storeIds: Set<number>;
      storesList: Array<{
        storeId: number;
        storeName: string;
        auditName: string;
        status: string;
        expectedQuantity: number;
        complianceScore: number | null;
        findingCount: number;
      }>;
    }>();

    for (const store of globalStores) {
      if (!store.auditorId) continue;
      const auditorId = Number(store.auditorId);
      const existing = globalAuditorMap.get(auditorId) ?? {
        auditorId,
        auditorName: store.auditorName || 'Unknown Auditor',
        completedStores: 0,
        inProgressStores: 0,
        pendingStores: 0,
        totalStores: 0,
        expectedQuantity: 0,
        verifiedQuantity: 0,
        findingCount: 0,
        verifiedCount: 0,
        storeIds: new Set<number>(),
        storesList: [] as Array<{
          storeId: number;
          storeName: string;
          auditName: string;
          status: string;
          expectedQuantity: number;
          complianceScore: number | null;
          findingCount: number;
        }>,
      };

      existing.totalStores++;
      if (store.responseStatus === 'submitted' || store.responseStatus === 'reviewed') {
        existing.completedStores++;
      } else if (store.responseStatus === 'in_progress') {
        existing.inProgressStores++;
      } else {
        existing.pendingStores++;
      }

      existing.expectedQuantity += store.expectedQuantity;
      existing.findingCount += store.findingCount;

      existing.storeIds.add(store.storeId);
      existing.storesList.push({
        storeId: store.storeId,
        storeName: store.storeName,
        auditName: store.auditName,
        status: store.responseStatus,
        expectedQuantity: store.expectedQuantity,
        complianceScore: store.complianceScore,
        findingCount: store.findingCount,
      });

      globalAuditorMap.set(auditorId, existing);
    }

    // Accumulate actual quantities verified by auditor
    for (const line of globalCampaignLines) {
      const storeDetails = globalStores.find(s => s.storeId === line.storeId && s.auditId === line.auditId);
      if (storeDetails && storeDetails.auditorId) {
        const auditorId = Number(storeDetails.auditorId);
        const existing = globalAuditorMap.get(auditorId);
        if (existing) {
          if (line.verifiedQuantity !== null) {
            existing.verifiedQuantity += line.verifiedQuantity;
            existing.verifiedCount++;
          }
        }
      }
    }

    const aggregatedAuditors = Array.from(globalAuditorMap.values()).sort((a, b) =>
      a.auditorName.localeCompare(b.auditorName)
    );

    return (
      <main className="min-h-screen bg-gray-50 p-6">
        <div className="space-y-6">
          <PageHeader
            icon={ClipboardCheck}
            title="Audits"
            description="Manage audit campaigns, completed audits and QA findings."
            className="md:justify-between"
          >
            <div className="relative min-w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input className="pl-9" placeholder="Search audits..." />
            </div>
            <Button variant="outline" onClick={() => navigate('/admin/audits/templates')}>
              <FileText className="mr-2 h-4 w-4" />
              Audit Templates
            </Button>
            <Button
              onClick={() => {
                setCurrentStep(1);
                navigate('/admin/audits/new');
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Audit
            </Button>
          </PageHeader>

          <Card className="shadow-sm border-gray-200">
            <CardHeader className="flex flex-col gap-4 border-b border-gray-100 pb-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-2">
                <div>
                  <CardTitle>Audit Dashboard</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Aggregate audit performance, store status, campaign counts and auditor workloads.
                  </p>
                </div>
                {activeLandingTab === 'audits' && (
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs">{auditCampaigns.length} campaigns</Badge>
                )}
                {activeLandingTab === 'stores' && (
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs">{globalStores.length} stores</Badge>
                )}
                {activeLandingTab === 'campaigns' && (
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs">{aggregatedCampaigns.length} campaigns</Badge>
                )}
                {activeLandingTab === 'auditors' && (
                  <Badge variant="secondary" className="px-2.5 py-1 text-xs">{aggregatedAuditors.length} auditors</Badge>
                )}
              </div>

              {/* Tab Switcher */}
              <div className="flex border-b border-gray-200 -mx-6 px-6">
                <button
                  type="button"
                  onClick={() => {
                    setActiveLandingTab('audits');
                    setLandingSelectedCampaignKey(null);
                    setLandingSelectedAuditorId(null);
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${
                    activeLandingTab === 'audits'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  By Audit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveLandingTab('stores');
                    setLandingSelectedCampaignKey(null);
                    setLandingSelectedAuditorId(null);
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${
                    activeLandingTab === 'stores'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  By Store
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveLandingTab('campaigns');
                    setLandingSelectedCampaignKey(null);
                    setLandingSelectedAuditorId(null);
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${
                    activeLandingTab === 'campaigns'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  By Campaign
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveLandingTab('auditors');
                    setLandingSelectedCampaignKey(null);
                    setLandingSelectedAuditorId(null);
                  }}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${
                    activeLandingTab === 'auditors'
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  By Auditor
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {overviewQuery.isError && (
                <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <span>Couldn't load audit data. Some lists may be empty or out of date.</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => overviewQuery.refetch()}>
                    Retry
                  </Button>
                </div>
              )}
              {activeLandingTab === 'audits' && (() => {
                const statusBucket = (status: string): 'draft' | 'sent' | 'completed' => {
                  // 'planned' is the legacy pre-dispatch create status: those audits
                  // were never sent (no dispatches), so they belong with drafts.
                  if (status === 'draft' || status === 'planned') return 'draft';
                  if (status === 'submitted' || status === 'reviewed' || status === 'closed') return 'completed';
                  return 'sent';
                };
                const isArchived = (a: any) => Boolean(a.archivedAt);
                const activeAudits = auditCampaigns.filter((a: any) => !isArchived(a));
                const archivedAudits = auditCampaigns.filter(isArchived);
                const filterOptions: Array<{ key: typeof auditStatusFilter; label: string }> = [
                  { key: 'all', label: 'All' },
                  { key: 'draft', label: 'Drafts' },
                  { key: 'sent', label: 'Sent' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'archived', label: 'Archived' },
                ];
                const countFor = (key: typeof auditStatusFilter) => {
                  if (key === 'archived') return archivedAudits.length;
                  if (key === 'all') return activeAudits.length;
                  return activeAudits.filter((a: any) => statusBucket(a.status) === key).length;
                };
                const filteredAuditCampaigns =
                  auditStatusFilter === 'archived'
                    ? archivedAudits
                    : auditStatusFilter === 'all'
                      ? activeAudits
                      : activeAudits.filter((a: any) => statusBucket(a.status) === auditStatusFilter);

                return (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.map((opt) => (
                        <Button
                          key={opt.key}
                          type="button"
                          size="sm"
                          variant={auditStatusFilter === opt.key ? 'default' : 'outline'}
                          onClick={() => setAuditStatusFilter(opt.key)}
                        >
                          {opt.label}
                          <Badge variant="secondary" className="ml-2">{countFor(opt.key)}</Badge>
                        </Button>
                      ))}
                    </div>
                    <DataTable
                      columns={auditsColumns}
                      data={filteredAuditCampaigns}
                      searchKey="name"
                      searchPlaceholder="Search audits..."
                      showColumnToggle={false}
                      isLoading={overviewQuery.isLoading}
                    />
                  </div>
                );
              })()}

              {activeLandingTab === 'stores' && (
                <DataTable
                  columns={storesColumns}
                  data={globalStores}
                  searchKey="storeName"
                  searchPlaceholder="Search stores..."
                  showColumnToggle={false}
                  isLoading={overviewQuery.isLoading}
                />
              )}

              {activeLandingTab === 'campaigns' && (() => {
                const selectedCampaign = aggregatedCampaigns.find(c => `${c.campaignId ?? c.campaignName}` === landingSelectedCampaignKey);

                return (
                  <div className="grid gap-6 lg:grid-cols-[1fr_450px]">
                    <div className="overflow-x-auto border rounded-lg bg-white">
                      <DataTable
                        columns={campaignsColumns}
                        data={aggregatedCampaigns}
                        searchKey="campaignName"
                        searchPlaceholder="Search campaigns..."
                        showColumnToggle={false}
                        isLoading={overviewQuery.isLoading}
                        onRowClick={(row) => {
                          const key = `${row.campaignId ?? row.campaignName}`;
                          setLandingSelectedCampaignKey(landingSelectedCampaignKey === key ? null : key);
                        }}
                        getRowClassName={(row) => {
                          const key = `${row.campaignId ?? row.campaignName}`;
                          return key === landingSelectedCampaignKey ? "bg-primary/5 hover:bg-primary/5 font-semibold" : "";
                        }}
                      />
                    </div>

                    <div className="border rounded-lg p-4 bg-gray-50/50 min-h-[300px]">
                      {selectedCampaign ? (
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg">{selectedCampaign.campaignName}</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">Audited stores and formats detail</p>
                          </div>
                          <div className="max-h-[500px] overflow-y-auto space-y-3 pr-1">
                            {selectedCampaign.lines.map((line, idx) => (
                              <div key={idx} className="bg-white border rounded-md p-3 shadow-xs space-y-2">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h5 className="font-semibold text-sm text-gray-900">{line.storeName}</h5>
                                    <p className="text-xs text-muted-foreground">{line.auditName}</p>
                                  </div>
                                  <Badge variant={line.condition === 'damaged' ? 'destructive' : 'outline'} className="text-[10px]">
                                    {line.condition || 'no status'}
                                  </Badge>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center border-y py-1.5 text-xs">
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Approved</p>
                                    <p className="font-bold text-gray-800">{line.approvedQuantity}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Verified</p>
                                    <p className={`font-bold ${line.submittedQuantity !== line.approvedQuantity ? 'text-red-600' : 'text-green-600'}`}>
                                      {line.submittedQuantity ?? '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Format</p>
                                    <p className="font-medium text-gray-800 truncate" title={line.formatName}>
                                      {line.formatName}
                                    </p>
                                  </div>
                                </div>
                                {line.comments && (
                                  <p className="text-[11px] bg-amber-50 text-amber-800 px-2 py-1 rounded border border-amber-100 italic">
                                    "{line.comments}"
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-6">
                          <p className="font-medium">Select a campaign</p>
                          <p className="text-xs mt-1">Click a campaign in the list to drill down into format verification lines and store status.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {activeLandingTab === 'auditors' && (() => {
                const selectedAuditor = aggregatedAuditors.find(a => a.auditorId === landingSelectedAuditorId);

                return (
                  <div className="grid gap-6 lg:grid-cols-[1fr_450px]">
                    <div className="overflow-x-auto border rounded-lg bg-white">
                      <DataTable
                        columns={auditorsColumns}
                        data={aggregatedAuditors}
                        searchKey="auditorName"
                        searchPlaceholder="Search auditors..."
                        showColumnToggle={false}
                        isLoading={overviewQuery.isLoading}
                        onRowClick={(row) => {
                          setLandingSelectedAuditorId(landingSelectedAuditorId === row.auditorId ? null : row.auditorId);
                        }}
                        getRowClassName={(row) => {
                          return row.auditorId === landingSelectedAuditorId ? "bg-primary/5 hover:bg-primary/5 font-semibold" : "";
                        }}
                      />
                    </div>

                    <div className="border rounded-lg p-4 bg-gray-50/50 min-h-[300px]">
                      {selectedAuditor ? (
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg">{selectedAuditor.auditorName}</h4>
                            <p className="text-xs text-muted-foreground mt-0.5">Store workload assignments</p>
                          </div>
                          <div className="max-h-[500px] overflow-y-auto space-y-3 pr-1">
                            {selectedAuditor.storesList.map((store, idx) => (
                              <div key={idx} className="bg-white border rounded-md p-3 shadow-xs space-y-2">
                                <div className="flex items-center justify-between">
                                  <h5 className="font-semibold text-sm text-gray-900">{store.storeName}</h5>
                                  <Badge variant={
                                    store.status === 'submitted' || store.status === 'reviewed' ? 'default' :
                                    store.status === 'in_progress' ? 'secondary' : 'outline'
                                  } className="text-[10px]">
                                    {store.status.replaceAll('_', ' ')}
                                  </Badge>
                                </div>
                                <p className="text-[10px] text-muted-foreground">{store.auditName}</p>
                                <div className="grid grid-cols-3 gap-2 text-center pt-1 text-xs">
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Expected</p>
                                    <p className="font-semibold text-gray-800">{store.expectedQuantity}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Findings</p>
                                    <p className="font-semibold text-red-600">{store.findingCount}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground text-[10px] uppercase font-semibold">Score</p>
                                    <p className="font-semibold text-green-600">{store.complianceScore === null ? '-' : `${store.complianceScore}%`}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground p-6">
                          <p className="font-medium">Select an auditor</p>
                          <p className="text-xs mt-1">Click an auditor in the list to inspect their active store workload, completions, and findings.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="space-y-6">
        <PageHeader
          icon={ClipboardCheck}
          title="Create Audit Campaign"
          description="Build an independent audit from locked cycle data, then assign auditors per store."
          className="md:justify-between"
        >
          <Button variant="outline" onClick={() => navigate('/admin/audits')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Audits
          </Button>
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between mb-4">
              <span>{getStepTitle()}</span>
              <Badge variant="outline">{currentStep}/6</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-6">
            <Stepper
              steps={auditSteps}
              currentStep={currentStep - 1}
              onStepClick={handleStepClick}
            />
          </CardContent>
        </Card>

        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Audit Setup
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Name the audit campaign and choose the default audit structure.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Audit campaign name</Label>
                <Input
                  value={draftName}
                  onChange={(event) => setDraftName(event.target.value)}
                  placeholder={selectedCycle ? `${selectedCycle.retailerName} ${selectedCycle.cycleName} audit` : 'Cycle audit'}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-[240px_1fr]">
                <div className="space-y-2">
                  <Label>Audit start date</Label>
                  <Input
                    type="date"
                    value={auditStartDate}
                    onChange={(event) => setAuditStartDate(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Audit type</Label>
                  <Select value={auditType} onValueChange={(value) => setAuditType(value as AuditType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AUDIT_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Choose Retailer
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Only retailers with locked cycles are available for audit planning.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {retailers.map((retailer) => (
                  <Card
                    key={retailer.id}
                    className={`cursor-pointer transition-colors p-4 ${selectedRetailerId === String(retailer.id) ? 'ring-2 ring-primary' : 'hover:bg-muted'}`}
                    onClick={() => {
                      setSelectedRetailerId(String(retailer.id));
                      setSelectedCycleId('');
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-white">
                          {retailer.logo ? (
                            <img
                              src={retailer.logo}
                              alt={`${retailer.name} logo`}
                              className="max-h-10 max-w-10 object-contain"
                            />
                          ) : (
                            <span className="text-sm font-semibold text-gray-500">
                              {retailer.name.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                        <p className="font-medium text-gray-900">{retailer.name}</p>
                        <p className="text-sm text-gray-500">{retailer.lockedCycleCount} locked cycles</p>
                        </div>
                      </div>
                      <Badge variant="secondary">{retailer.lockedCycleCount}</Badge>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 3 && (
          <Card>
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5" />
                  Select Locked Cycle
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Audits use approved operator proof submissions from the selected cycle.
                </p>
              </div>
              <Badge variant="secondary">{filteredCycles.length} available</Badge>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Cycle</TableHead>
                      <TableHead>Locked</TableHead>
                      <TableHead className="text-right">Stores</TableHead>
                      <TableHead className="text-right">Campaigns</TableHead>
                      <TableHead className="text-right">Approved qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCycles.map((cycle) => {
                      const isSelected = selectedCycleId === String(cycle.cycleId);
                      return (
                      <TableRow
                        key={cycle.lockId}
                        className={`cursor-pointer border-l-4 ${
                          isSelected
                            ? 'border-l-blue-600 bg-blue-100 hover:bg-blue-100'
                            : 'border-l-transparent hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedCycleId(String(cycle.cycleId))}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={isSelected}
                            aria-label={`Select ${cycle.cycleName}`}
                            onCheckedChange={() => setSelectedCycleId(String(cycle.cycleId))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isSelected
                              ? <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600" />
                              : <Store className="h-4 w-4 shrink-0 text-gray-400" />}
                            <div>
                              <p className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{cycle.cycleName}</p>
                              <p className="text-xs text-gray-500">{formatDate(cycle.startDate)} to {formatDate(cycle.endDate)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(cycle.lockedAt)}</TableCell>
                        <TableCell className="text-right">{cycle.storeCount}</TableCell>
                        <TableCell className="text-right">{cycle.campaignCount}</TableCell>
                        <TableCell className="text-right">{cycle.expectedQuantity}</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Select Stores
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Search stores with approved operator submissions in this cycle.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  value={storeSearch}
                  onChange={(event) => setStoreSearch(event.target.value)}
                  className="pl-9"
                  placeholder="Find stores in this locked cycle"
                />
              </div>
              <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
                <div className="rounded-md border bg-white">
                  {storesQuery.isLoading && <p className="p-4 text-sm text-gray-500">Loading stores...</p>}
                  {storesQuery.isError && (
                    <div className="flex items-center justify-between gap-3 p-4 text-sm text-red-700">
                      <span>Couldn't load stores for this cycle.</span>
                      <Button type="button" variant="outline" size="sm" onClick={() => storesQuery.refetch()}>
                        Retry
                      </Button>
                    </div>
                  )}
                  {!storesQuery.isLoading && !storesQuery.isError && cycleStores.length === 0 && <p className="p-4 text-sm text-gray-500">No stores match this cycle/search.</p>}
                  {cycleStores.map((store) => {
                    const selected = selectedStores.some((selectedStore) => selectedStore.id === store.id);
                    return (
                      <button
                        key={store.id}
                        type="button"
                        className="flex w-full items-start justify-between gap-3 border-b px-4 py-3 text-left last:border-b-0 hover:bg-gray-50"
                        onClick={() => {
                          setSelectedStores((current) =>
                            selected ? current.filter((selectedStore) => selectedStore.id !== store.id) : [...current, store],
                          );
                        }}
                      >
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{store.name}</span>
                          <span className="block text-xs text-gray-500">{store.address || store.region || 'No address'}</span>
                          <span className="block text-xs text-gray-500">{store.campaignCount} campaigns, approved qty {store.expectedQuantity}</span>
                        </span>
                        {selected ? <X className="h-4 w-4 text-gray-500" /> : <Plus className="h-4 w-4 text-gray-500" />}
                      </button>
                    );
                  })}
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Selected stores</CardTitle>
                    <p className="text-sm text-muted-foreground">{selectedStores.length} stores selected</p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedStores.length === 0 && <p className="text-sm text-gray-500">No stores selected yet.</p>}
                    {selectedStores.map((store) => (
                      <div key={store.id} className="flex items-center justify-between gap-3 rounded-md border p-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{store.name}</p>
                          <p className="text-xs text-gray-500">Approved qty {store.expectedQuantity}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedStores((current) => current.filter((item) => item.id !== store.id))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Assign Auditors
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Assign an independent auditor per selected store.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-white">
                <div className="border-b px-4 py-3">
                  <p className="text-sm font-medium text-gray-900">
                    {assignedStoreCount} of {selectedStores.length} stores assigned
                    {conflictCount > 0 ? `, ${conflictCount} independence conflict${conflictCount === 1 ? '' : 's'}` : ''}
                  </p>
                </div>
                {auditorsQuery.isError && (
                  <div className="flex items-center justify-between gap-3 border-b bg-red-50 px-4 py-3 text-sm text-red-700">
                    <span>Couldn't load auditors. Assignment options are unavailable.</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => auditorsQuery.refetch()}>
                      Retry
                    </Button>
                  </div>
                )}
                {selectedStores.map((store) => {
                  const assignedAuditorId = storeAuditorIds[store.id] ?? '';
                  const assignedAuditor = auditors.find((auditor) => String(auditor.id) === assignedAuditorId) ?? null;
                  const hasConflict = assignedAuditor
                    ? store.originalOperators.some((operator) => operator.id === assignedAuditor.id)
                    : false;

                  return (
                    <div key={store.id} className="grid gap-4 border-b p-4 last:border-b-0 lg:grid-cols-[1fr_360px]">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{store.name}</p>
                        <p className="text-xs text-gray-500">{store.address || store.region || 'No address'}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {store.campaignCount} campaigns, approved qty {store.expectedQuantity}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Original operator: {store.originalOperators.length > 0
                            ? store.originalOperators.map((operator) => operator.name).join(', ')
                            : 'Unknown'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <select
                          value={assignedAuditorId}
                          onChange={(event) => {
                            const auditorId = event.target.value;
                            setStoreAuditorIds((current) => ({ ...current, [store.id]: auditorId }));
                          }}
                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="">Select auditor for this store</option>
                          {auditors.map((auditor) => {
                            const blocked = store.originalOperators.some((operator) => operator.id === auditor.id);
                            return (
                              <option key={auditor.id} value={String(auditor.id)} disabled={blocked}>
                                {auditor.name}{auditor.region ? ` - ${auditor.region}` : ''}{blocked ? ' (original operator - unavailable)' : ''}
                              </option>
                            );
                          })}
                        </select>
                        {hasConflict && (
                          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                            {assignedAuditor?.name} cannot independently audit this store because they are listed as an original operator.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 6 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5" />
                Review Audit Campaign
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Review the audit campaign before creating it.
              </p>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-dashed bg-gray-50 p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-900">{auditCampaignName}</p>
                <p className="mt-1">
                  {selectedCycle
                    ? `${selectedCycle.retailerName} / ${selectedCycle.cycleName}: starts ${formatDate(auditStartDate)}, ${AUDIT_TYPE_LABELS[auditType]}, ${selectedStores.length} selected stores, ${assignedStoreCount} store auditor assignments${conflictCount > 0 ? `, ${conflictCount} conflicts to fix` : ''}.`
                    : 'Select a locked cycle to preview audit coverage.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          {currentStep < 6 ? (
            <Button onClick={nextStep} disabled={!canProceed()}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => createAuditMutation.mutate()}
              disabled={!canProceed() || createAuditMutation.isPending}
            >
              {createAuditMutation.isPending ? 'Creating Audit...' : 'Create Audit Campaign'}
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}
